/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Users, Award, Link, Key, X, Trash2, Edit2, MessageCircle, RefreshCw, Upload, Image, Shield,
  Star, Mail, Phone, Briefcase, CreditCard, MapPin, ChevronRight, Send,
  CheckCircle, XCircle, ArrowUp, ArrowDown, RotateCcw, Circle
} from 'react-feather';
import AuthService from '../../services/authService';
import StudioAgentDetailsModal from './StudioAgentDetailsModal';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

/**
 * AGENT DETAILS MODAL
 *
 * Pulled out of CreateAgentForm (which was ~4800 lines). This component owns all
 * of its own data — pools, team, users, invitations, QR, listings sync, and the
 * priority/activity feed. The parent only passes the agent and the handlers that
 * open OTHER modals.
 *
 * Tabs: Overview · Territories · Users · Invitations · Activity
 *
 * ACTIVITY is admin-only. Agents never see their own priority tier — surfacing it
 * would just teach them to game the notification queue.
 */

// Lead tier badge styling. Mirrors backend utils/pointsConfig.js.
const TIER_STYLES = {
  platinum: { bg: 'rgba(168, 85, 247, 0.15)', fg: '#7c3aed' },
  gold:     { bg: 'rgba(234, 179, 8, 0.15)',  fg: '#a16207' },
  silver:   { bg: 'rgba(148, 163, 184, 0.2)', fg: '#475569' },
  bronze:   { bg: 'rgba(180, 83, 9, 0.15)',   fg: '#92400e' },
  flex:     { bg: 'rgba(209, 213, 219, 0.25)', fg: '#4b5563' },
};
const tierStyle = (tier) => TIER_STYLES[(tier || '').toLowerCase()] || TIER_STYLES.flex;

// Activity feed — icon + left-border accent per event type.
// `Icon` is a react-feather component, rendered in the activity row. Feather is
// outline-only, so the row reads its status from `color` + the left border.
const ACTIVITY_META = {
  notified:      { Icon: Mail,        color: '#9ca3af' },
  claim_success: { Icon: CheckCircle, color: '#16a34a' },
  claim_failed:  { Icon: XCircle,     color: '#dc2626' },
  tier_up:       { Icon: ArrowUp,     color: '#2563eb' },
  tier_reset:    { Icon: RotateCcw,   color: '#16a34a' },
  tier_decay:    { Icon: ArrowDown,   color: '#d97706' },
};

const ACTIVITY_FILTERS = [
  { key: '',                              label: 'All' },
  { key: 'notified',                      label: 'Notified' },
  { key: 'claim_success',                 label: 'Claimed' },
  { key: 'claim_failed',                  label: 'Failed' },
  { key: 'tier_up,tier_reset,tier_decay', label: 'Tier Changes' },
];

const formatResponseTime = (seconds) => {
  if (seconds == null) return '\u2014';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
};

const hasValidProfileImage = (agent) =>
  !!agent?.profileImage &&
  agent.profileImage !== 'null' &&
  agent.profileImage !== 'undefined' &&
  agent.profileImage.trim() !== '';


/* ---------------- layout ---------------- */
const styles = {
  overlay: {
    position: 'fixed',
    top: 0, right: 0, bottom: 0,   // `left` is applied at render — see sidebarOffset
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999999,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    width: '100%',        // fills the (already sidebar-offset) overlay; 85vw would ignore the offset and overflow right
    height: '95vh',
    maxWidth: '1600px',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #e5e7eb',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.18)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #f0f0f0',
    flexShrink: 0,
  },
  content: {
    display: 'flex',
    gap: '24px',
    padding: '24px',
    flex: 1,
    minHeight: 0,          // lets the columns scroll instead of blowing out the modal
  },
  left: {
    width: '35%',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    overflowY: 'auto',
    paddingRight: '4px',
  },
  right: {
    width: '65%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f7f7f8',
    padding: '20px 24px',
    borderRadius: '12px',
    overflowY: 'auto',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
  },
  sectionCard: {
    background: '#fafafa',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid #f0f0f0',
  },
};

// Studio-only account: entitlements has 'studio' and NOT 'pro'.
//
// FAILS SAFE. If `entitlements` isn't on the agent payload at all (an admin
// list projection that doesn't select it), this returns false and everyone
// gets the Pro modal exactly as before — the current behaviour, not a blank
// screen.
const isStudioOnly = (agent) => {
  const ents = agent?.entitlements;
  if (!Array.isArray(ents)) return false;
  return ents.includes('studio') && !ents.includes('pro');
};

// Router, not a component. A Studio account shares almost nothing with a Pro
// agent — no pools, no leads, no points, no invitations — so it gets its own
// modal rather than ten tabs of empty state inside this one.
//
// A DUAL-ENTITLEMENT AGENT KEEPS THIS MODAL. They're a Pro agent who also
// bought Studio; their territories and lead pipeline are still the main event,
// and the Studio tab here covers their credits.
const AgentDetailsModal = (props) => {
  if (isStudioOnly(props.agent)) {
    return (
      <StudioAgentDetailsModal
        agent={props.agent}
        onClose={props.onClose}
        onEdit={props.onEdit}
        onAgentDeleted={props.onAgentDeleted}
        onMessage={props.onMessage}
        onResetPassword={props.onResetPassword}
      />
    );
  }
  return <ProAgentDetailsModal {...props} />;
};

const ProAgentDetailsModal = ({
  agent,                  // selectedAgentForDetails
  onClose,
  onAgentDeleted,         // refresh the agent list after a delete
  onEdit,                 // openEditAgentModal(agent)
  onAddPoints,            // openAddPointsModal(agent)
  onMessage,              // openMessageModal(agent)
  onReferrals,            // openReferralModal(agent)
  onResetPassword,        // openResetModal(agent)
  onViewUser,             // openUserDetailsModal(user)
  onRefundUser,           // handleRefundUser(user)
  onAgentUpdated,         // parent resync after an in-place agent mutation (QR, etc.)
}) => {
  // Local editable copy. QR uploads mutate the agent in place; a local copy lets
  // that render immediately instead of waiting on a parent refetch.
  const [localAgent, setLocalAgent] = useState(agent);
  useEffect(() => { setLocalAgent(agent); }, [agent]);
  const selectedAgentForDetails = localAgent;
  const setSelectedAgentForDetails = (next) => {
    setLocalAgent(prev => {
      const updated = typeof next === 'function' ? next(prev) : next;
      onAgentUpdated?.(updated);
      return updated;
    });
  };

  // The overlay is position:fixed, so it centers on the VIEWPORT and slides
  // under the floating sidebar. Offset it by the sidebar's real right edge —
  // getBoundingClientRect().right already accounts for the sidebar's own left
  // margin, so the "floating" gap is covered too. On mobile the sidebar is
  // off-canvas (right edge <= 0), so this collapses to 0 on its own.
  const [sidebarOffset, setSidebarOffset] = useState(0);
  useEffect(() => {
    const measure = () => {
      const el = document.querySelector('.dashboard-sidebar');
      const right = el ? el.getBoundingClientRect().right : 0;
      setSidebarOffset(right > 0 ? right : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const [activeTab, setActiveTab] = useState('territories');

  // Integrations (agent-supplied API keys). The list only carries last4 —
  // the full key is fetched per provider on "Reveal" and dropped on agent change.
  const [integrations, setIntegrations] = useState([]);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(false);
  const [integrationsError, setIntegrationsError] = useState('');
  const [revealedKeys, setRevealedKeys] = useState({});   // { provider: { credentials, labels } }
  const [revealingProvider, setRevealingProvider] = useState(null);
  const [copiedProvider, setCopiedProvider] = useState(null); // "provider:field"
  const [testingProvider, setTestingProvider] = useState(null);
  const [testResults, setTestResults] = useState({});   // { provider: result }

  // PM plan + monthly spend cap (lease accounts). Storage only.
  const [pmBilling, setPmBilling] = useState(null);
  const [pmPlans, setPmPlans] = useState({});
  const [pmDraftPlan, setPmDraftPlan] = useState('');
  const [pmDraftCap, setPmDraftCap] = useState('');   // '' = plan base (null)
  const [isLoadingPm, setIsLoadingPm] = useState(false);
  const [isSavingPm, setIsSavingPm] = useState(false);
  const [pmError, setPmError] = useState('');
  const [pmSaved, setPmSaved] = useState(false);

  // Onboarding link.
  // The token is stored hashed server-side, so there's no "show me the current
  // link" — regenerating is the only way to get a usable URL, and it comes back
  // exactly once. `regeneratedLink` holds that one-shot value for copying; it is
  // deliberately NOT persisted anywhere.
  const [onboarding, setOnboarding] = useState(null);
  const [isLoadingOnboarding, setIsLoadingOnboarding] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isTogglingOnboarding, setIsTogglingOnboarding] = useState(false);
  const [regeneratedLink, setRegeneratedLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [onboardingError, setOnboardingError] = useState('');
  const [onboardingSuccess, setOnboardingSuccess] = useState('');

  // Scheduled onboarding SESSIONS — the calendar call, not the setup link above.
  // Two different things that both say "onboarding": the block above is the
  // token that lets the agent set a password, this is the meeting the AE booked
  // off a Closed Won demo.
  //
  // These live in their own collection and are matched to an account by email,
  // then phone. When the Stripe checkout email differs from the demo email that
  // match finds nothing, and CS ends up holding an onboarding with no account —
  // which kills Pools, Unlock, and the access email on their card. This section
  // is the manual repair for that.
  const [agentOnboardings, setAgentOnboardings] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [unlinkedSessions, setUnlinkedSessions] = useState([]);
  const [isLoadingUnlinked, setIsLoadingUnlinked] = useState(false);
  const [showSessionPicker, setShowSessionPicker] = useState(false);
  const [sessionSearch, setSessionSearch] = useState('');
  const [linkingId, setLinkingId] = useState(null);
  const [sessionError, setSessionError] = useState('');
  const [sessionNotice, setSessionNotice] = useState('');

  // Assigned users
  const [agentDetailsUsers, setAgentDetailsUsers] = useState([]);
  const [isLoadingAgentDetails, setIsLoadingAgentDetails] = useState(false);

  // Pools / territories
  const [allPools, setAllPools] = useState([]);
  const [agentPools, setAgentPools] = useState([]);
  const [isLoadingPools, setIsLoadingPools] = useState(false);
  const [isUpdatingPool, setIsUpdatingPool] = useState(false);
  const [poolSearchTerm, setPoolSearchTerm] = useState('');
  const [showPoolDropdown, setShowPoolDropdown] = useState(false);
  const [presetMissingPools, setPresetMissingPools] = useState([]);
  const [userPresets, setUserPresets] = useState([]);
  const [isApplyingPreset, setIsApplyingPreset] = useState(false);
  const [presetSearchTerm, setPresetSearchTerm] = useState('');
  const [showPresetDropdown, setShowPresetDropdown] = useState(false);
  const poolDropdownRef = useRef(null);

  // Pool requests the agent made during onboarding. These are NOT memberships —
  // they sit in the PoolRequest collection until approved here, because writing
  // into AgentPool.agents is what starts their lead emails.
  const [poolRequests, setPoolRequests] = useState([]);
  const [poolsDeferred, setPoolsDeferred] = useState(false);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [decidingKey, setDecidingKey] = useState(null);
  const pendingRequestCount = poolRequests.filter(r => r.status === 'pending').length;
  const presetDropdownRef = useRef(null);

  // Team lead
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(false);
  const [isUpdatingTeamLead, setIsUpdatingTeamLead] = useState(false);
  const [teamLeadError, setTeamLeadError] = useState('');
  const [teamLeadSuccess, setTeamLeadSuccess] = useState('');
  const [agents, setAgents] = useState([]);

  // Invitations
  const [agentInvitations, setAgentInvitations] = useState([]);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);
  const [resendingInviteId, setResendingInviteId] = useState(null);

  // QR code
  const [isUploadingQR, setIsUploadingQR] = useState(false);
  const [qrError, setQrError] = useState('');
  const [qrSuccess, setQrSuccess] = useState('');
  const qrFileInputRef = useRef(null);

  // Refund banners + in-flight row. The refund ITSELF is the parent's
  // (onRefundUser opens the confirm modal there); these just render state
  // the lifted Assigned-Users JSX reads.
  const [refundingUserId, setRefundingUserId] = useState(null);
  const [refundSuccess, setRefundSuccess] = useState('');
  const [refundError, setRefundError] = useState('');

  // Studio credits (only meaningful for entitlements including 'studio')
  const [studioCredits, setStudioCredits] = useState(null);
  const [isLoadingStudio, setIsLoadingStudio] = useState(false);
  const [studioDelta, setStudioDelta] = useState('');
  const [studioReason, setStudioReason] = useState('');
  const [isAdjustingStudio, setIsAdjustingStudio] = useState(false);
  const [studioError, setStudioError] = useState('');
  const [studioSuccess, setStudioSuccess] = useState('');

  // Listings sync
  const [isSyncingListings, setIsSyncingListings] = useState(false);
  const [syncListingsResult, setSyncListingsResult] = useState(null);
  const [syncListingsError, setSyncListingsError] = useState('');

  // Delete
  const [isDeleting, setIsDeleting] = useState(false);

  // Referral links (the Overview stats card counts them)
  const [referralLinks, setReferralLinks] = useState([]);

  // Priority queue + activity feed (ADMIN ONLY)
  const [agentPriority, setAgentPriority] = useState(null);
  const [emailStats, setEmailStats] = useState(null);

  // Notification settings (admin-editable)
  const [notifSettings, setNotifSettings] = useState(null);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifError, setNotifError] = useState('');
  const [assistantDraft, setAssistantDraft] = useState('');
  const [agentActivity, setAgentActivity] = useState([]);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [activityFilter, setActivityFilter] = useState('');
  const [activityPage, setActivityPage] = useState(1);
  const [activityHasMore, setActivityHasMore] = useState(false);

  // Generic banners some of the lifted handlers write to.
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  /* ---------------- data loading ---------------- */

  // This agent's referral links — only used for the Overview count.
  const fetchReferralLinks = async (agentId) => {
    try {
      const response = await fetch(`${API_URL}/referrals/agent/${agentId}`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (data.success) setReferralLinks(data.data || []);
    } catch (err) {
      console.error('Error fetching referral links:', err);
    }
  };

  const fetchAgentDetailsUsers = async (agentId) => {
    setIsLoadingAgentDetails(true);
    try {
      const response = await fetch(`${API_URL}/admin/agents/${agentId}/assigned-users`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (response.ok && data.success) setAgentDetailsUsers(data.data || []);
    } catch (err) {
      console.error('Error fetching assigned users:', err);
    } finally {
      setIsLoadingAgentDetails(false);
    }
  };

  // Agent roster — only needed for the "assign to team lead" dropdown.
  const fetchAgents = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/agents`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (data.success) setAgents(data.data || []);
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  // ---- Priority + activity (the lead notification queue) ----

  const fetchAgentPriority = async (agentId) => {
    try {
      const response = await fetch(`${API_URL}/admin/agents/${agentId}/priority`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (data.success) setAgentPriority(data.data);
    } catch (err) {
      console.error('Error fetching agent priority:', err);
    }
  };

  // Lead-alert email engagement. Send counts come from LeadNotification (full
  // history); opens/clicks come from EmailEvent (only since the SendGrid event
  // webhook went live), which is why the response carries trackingSince.
  const fetchAgentEmailStats = async (agentId) => {
    try {
      const response = await fetch(`${API_URL}/admin/agents/${agentId}/email-stats?days=30`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (data.success) setEmailStats(data.data);
    } catch (err) {
      console.error('Error fetching agent email stats:', err);
    }
  };

  const fetchNotifSettings = async (agentId) => {
    try {
      const response = await fetch(`${API_URL}/admin/agents/${agentId}/notifications`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (data.success) {
        setNotifSettings(data.data);
        setAssistantDraft(data.data.assistantEmail || '');
      }
    } catch (err) {
      console.error('Error fetching notification settings:', err);
    }
  };

  // Send only what changed — all three fields are optional on the endpoint.
  const saveNotifSettings = async (patch) => {
    setNotifSaving(true);
    setNotifError('');
    try {
      const response = await fetch(`${API_URL}/admin/agents/${agent._id}/notifications`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify(patch)
      });
      const data = await response.json();
      if (data.success) {
        setNotifSettings(data.data);
        setAssistantDraft(data.data.assistantEmail || '');
      } else {
        setNotifError(data.message || 'Failed to save');
      }
    } catch (err) {
      setNotifError(err.message);
    } finally {
      setNotifSaving(false);
    }
  };

  // `append` = "Load more"; otherwise replaces the feed.
  const fetchAgentActivity = async (agentId, page = 1, filter = '', append = false) => {
    if (!append) setIsLoadingActivity(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (filter) params.set('type', filter);

      const response = await fetch(`${API_URL}/admin/agents/${agentId}/activity?${params}`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();

      if (data.success) {
        setAgentActivity(prev => (append ? [...prev, ...data.data] : data.data));
        setActivityPage(page);
        setActivityHasMore(data.pagination?.hasMore || false);
      }
    } catch (err) {
      console.error('Error fetching agent activity:', err);
    } finally {
      setIsLoadingActivity(false);
    }
  };

  const handleDeleteAgent = async () => {
    if (!window.confirm(`Delete ${selectedAgentForDetails.name}? This cannot be undone.`)) return;
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_URL}/admin/agents/${selectedAgentForDetails._id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        onAgentDeleted?.();
        onClose();
      } else {
        setError(data.message || 'Failed to delete agent');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      setError('Error deleting agent');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchPoolRequests = async (agentId) => {
    setIsLoadingRequests(true);
    setRequestError('');
    try {
      const response = await fetch(`${API_URL}/admin/agents/${agentId}/pool-requests`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Could not load pool requests');
      }

      setPoolRequests(data.data.requests || []);
      setPoolsDeferred(!!data.data.poolsDeferred);
    } catch (err) {
      console.error('Error fetching pool requests:', err);
      setRequestError(err.message || 'Could not load pool requests');
    } finally {
      setIsLoadingRequests(false);
    }
  };

  /**
   * Approve or deny a batch of requests.
   *
   * Sequential, not Promise.all: a 23-pool county is 23 writes against
   * AgentPool. Firing them all at once to save a second on a screen someone is
   * reading is a bad trade.
   */
  const decideRequests = async (requests, status, key) => {
    if (decidingKey) return;

    if (status === 'denied') {
      const ok = window.confirm(
        `Deny ${requests.length} pool ${requests.length === 1 ? 'request' : 'requests'}?\n\n` +
        `The agent is never told — this is a silent decision.`
      );
      if (!ok) return;
    }

    setDecidingKey(key);
    setRequestError('');
    const decided = [];

    try {
      for (const request of requests) {
        const response = await fetch(`${API_URL}/admin/pool-requests/${request._id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${AuthService.getToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status })
        });
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Could not save that decision');
        }
        decided.push(request._id);
      }
    } catch (err) {
      console.error('Error deciding pool requests:', err);
      // Partial failure is real — say how far it got rather than implying
      // nothing happened.
      setRequestError(
        `${err.message}${decided.length ? ` (${decided.length} of ${requests.length} saved)` : ''}`
      );
    } finally {
      if (decided.length) {
        setPoolRequests(prev => prev.map(r =>
          decided.includes(r._id) ? { ...r, status } : r
        ));
        // Approvals change actual membership — pull the territory chips back in
        // so the list above reflects what just happened.
        if (status === 'approved') await fetchAgentPools(agent._id);
      }
      setDecidingKey(null);
    }
  };

  const fetchAllPools = async () => {
    try {
      const response = await fetch(`${API_URL}/agent-pools`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setAllPools(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching pools:', err);
    }
  };

  // Territory derivation removed — now uses agent.agentArea directly from the User model.

  const fetchAgentPools = async (agentId) => {
    setIsLoadingPools(true);
    try {
      const response = await fetch(`${API_URL}/agent-pools/agent/${agentId}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setAgentPools(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching agent pools:', err);
    } finally {
      setIsLoadingPools(false);
    }
  };

  const addAgentToPool = async (poolId) => {
    if (!selectedAgentForDetails) return;
    setIsUpdatingPool(true);
    try {
      const response = await fetch(`${API_URL}/agent-pools/${poolId}/agents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ agentId: agent._id })
      });
      const data = await response.json();
      if (data.success) {
        await fetchAgentPools(agent._id);
      }
    } catch (err) {
      console.error('Error adding agent to pool:', err);
    } finally {
      setIsUpdatingPool(false);
    }
  };

  const removeAgentFromPool = async (poolId) => {
    if (!selectedAgentForDetails) return;
    setIsUpdatingPool(true);
    try {
      const response = await fetch(`${API_URL}/agent-pools/${poolId}/agents/${agent._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        await fetchAgentPools(agent._id);
      }
    } catch (err) {
      console.error('Error removing agent from pool:', err);
    } finally {
      setIsUpdatingPool(false);
    }
  };

  // Fetch user-defined pool presets (managed in Pool Management → Pool Presets)
  const fetchUserPresets = async () => {
    try {
      const response = await fetch(`${API_URL}/agent-pool-presets`, {
        headers: { Authorization: `Bearer ${AuthService.getToken()}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      setUserPresets(data.data || []);
    } catch (err) {
      console.error('Error fetching user presets:', err);
    }
  };

  // Apply a user-defined preset: add every pool in the preset to the selected agent.
  // Preset shape: { _id, name, pools: [{ _id, name, serviceArea } | ObjectId] }
  // Matches by pool ID, so missing pools (deleted since preset creation) are surfaced.
  const applyPreset = async (preset) => {
    if (!selectedAgentForDetails || !preset) return;
    setIsApplyingPreset(true);
    setPresetMissingPools([]);

    const presetPoolIds = (preset.pools || []).map(p => (typeof p === 'object' ? p._id : p));
    const matched = [];
    const notMatched = [];

    presetPoolIds.forEach(poolId => {
      const found = allPools.find(pool => String(pool._id) === String(poolId));
      if (found) {
        const alreadyIn = agentPools.some(ap => String(ap._id) === String(found._id));
        if (!alreadyIn) matched.push(found);
      } else {
        // The pool was likely deleted after the preset was created.
        // Try to surface a name if we had one populated on the preset.
        const populated = (preset.pools || []).find(p => typeof p === 'object' && String(p._id) === String(poolId));
        notMatched.push(populated?.name || `Missing pool (${String(poolId).slice(-6)})`);
      }
    });

    if (notMatched.length > 0) {
      console.warn(`[Preset: ${preset.name}] Pools not found in current pool list:`, notMatched);
      setPresetMissingPools(notMatched);
    }

    // Add matched pools one at a time
    for (const pool of matched) {
      try {
        await fetch(`${API_URL}/agent-pools/${pool._id}/agents`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${AuthService.getToken()}`
          },
          body: JSON.stringify({ agentId: agent._id })
        });
      } catch (err) {
        console.error(`Error adding pool ${pool.name} from preset:`, err);
      }
    }

    if (matched.length > 0) {
      await fetchAgentPools(agent._id);
    }

    setIsApplyingPreset(false);
  };

  const removeAllPools = async () => {
    if (!selectedAgentForDetails || agentPools.length === 0) return;

    // Destructive, unlogged, and irreversible from the UI — the only way back is
    // re-adding every pool by hand. Worth a stop, especially since the button
    // sits directly under two search fields where a stray click is plausible.
    const ok = window.confirm(
      `Remove ${selectedAgentForDetails.name} from all ${agentPools.length} ` +
      `${agentPools.length === 1 ? 'territory' : 'territories'}?\n\n` +
      `They'll stop receiving leads from every one of them. You'll have to add ` +
      `any back by hand.`
    );
    if (!ok) return;

    setIsApplyingPreset(true);
    for (const pool of agentPools) {
      try {
        await fetch(`${API_URL}/agent-pools/${pool._id}/agents/${agent._id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
        });
      } catch (err) {
        console.error(`Error removing pool ${pool.name}:`, err);
      }
    }
    await fetchAgentPools(agent._id);
    setIsApplyingPreset(false);
  };

  const fetchTeamMembers = async (agentId) => {
    setIsLoadingTeamMembers(true);
    try {
      const response = await fetch(`${API_URL}/agents/${agentId}/team-members`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setTeamMembers(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching team members:', err);
      setTeamMembers([]);
    } finally {
      setIsLoadingTeamMembers(false);
    }
  };

  const assignTeamLead = async (agentId, teamLeadId) => {
    setIsUpdatingTeamLead(true);
    setTeamLeadError('');
    setTeamLeadSuccess('');
    try {
      const response = await fetch(`${API_URL}/agents/${agentId}/team-lead`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ teamLeadId: teamLeadId || null })
      });
      const data = await response.json();
      if (data.success) {
        setTeamLeadSuccess(teamLeadId ? 'Team lead assigned successfully' : 'Team lead removed successfully');
        // Update the selected agent's data
        setSelectedAgentForDetails(prev => ({
          ...prev,
          teamLead: teamLeadId ? agents.find(a => a._id === teamLeadId) : null
        }));
        // Refresh agents list to update any team member counts
        
        setTimeout(() => setTeamLeadSuccess(''), 3000);
      } else {
        setTeamLeadError(data.error || 'Failed to update team lead');
      }
    } catch (err) {
      console.error('Error assigning team lead:', err);
      setTeamLeadError('Error updating team lead');
    } finally {
      setIsUpdatingTeamLead(false);
    }
  };

  // Helper to check if an agent is a team lead (has team members)
  const getTeamMemberCount = (agentId) => {
    return agents.filter(a => a.teamLead === agentId || a.teamLead?._id === agentId).length;
  };
  const handleSyncAgentListings = async (agent) => {
    if (!agent || !agent._id) return;

    const confirmed = window.confirm(
      `Sync MLS listings for ${agent.name}?\n\n` +
      `This searches the MLS by their email and phone for active listings, ` +
      `imports them into our database, and links them to this agent.\n\n` +
      `This may take 30-90 seconds depending on how many listings they have.`
    );
    if (!confirmed) return;

    setIsSyncingListings(true);
    setSyncListingsResult(null);
    setSyncListingsError('');

    try {
      const response = await fetch(`${API_URL}/mls/sync/agent/${agent._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to sync agent listings');
      }

      setSyncListingsResult(data);
    } catch (error) {
      setSyncListingsError(error.message || 'An error occurred while syncing listings');
    } finally {
      setIsSyncingListings(false);
    }
  };


  /* ---------------- studio credits ---------------- */

  // Balance + the manual adjustment history. Manual adjustments ONLY — the
  // Stripe grant on invoice.paid and the per-video charge aren't in this array,
  // so `netManual` answers "how much have we comped them", not "where did the
  // balance come from".
  const fetchStudioCredits = async (agentId) => {
    setIsLoadingStudio(true);
    setStudioError('');
    try {
      const response = await fetch(`${API_URL}/studio/admin/accounts/${agentId}/credits`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to load Studio credits');
      }
      setStudioCredits(data.data);
    } catch (error) {
      setStudioError(error.message || 'Failed to load Studio credits');
      setStudioCredits(null);
    } finally {
      setIsLoadingStudio(false);
    }
  };

  // Signed delta, never an absolute balance — the backend records what was
  // granted, and "set to 4" would throw that away.
  //
  // The button is disabled while in-flight because there is no idempotency key
  // on this endpoint: a double click is two grants. That's a deliberate
  // trade-off for a one-admin action, and this disable IS the guard.
  const submitStudioAdjustment = async () => {
    const amount = parseInt(studioDelta, 10);
    const reason = studioReason.trim();

    if (!Number.isInteger(amount) || amount === 0) {
      setStudioError('Enter a non-zero whole number (negative to deduct)');
      return;
    }
    if (!reason) {
      setStudioError('A reason is required');
      return;
    }

    const confirmed = window.confirm(
      `${amount > 0 ? 'Grant' : 'Deduct'} ${Math.abs(amount)} Studio credit` +
      `${Math.abs(amount) === 1 ? '' : 's'} ` +
      `${amount > 0 ? 'to' : 'from'} ${selectedAgentForDetails?.name || 'this account'}?\n\n` +
      `Reason: ${reason}\n\n` +
      `Each credit is one video render (~$4-5 of compute). This is logged ` +
      `against your admin account.`
    );
    if (!confirmed) return;

    setIsAdjustingStudio(true);
    setStudioError('');
    setStudioSuccess('');

    try {
      const response = await fetch(`${API_URL}/studio/admin/accounts/${agent._id}/credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ delta: amount, reason })
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Adjustment failed');
      }

      setStudioSuccess(
        `${amount > 0 ? 'Granted' : 'Deducted'} ${Math.abs(amount)} credit` +
        `${Math.abs(amount) === 1 ? '' : 's'} - balance is now ${data.data.credits}`
      );
      setStudioDelta('');
      setStudioReason('');
      // Refetch rather than patching local state: the history row is written
      // server-side and we want the stored `balanceAfter`, not a guess.
      fetchStudioCredits(agent._id);
    } catch (error) {
      setStudioError(error.message || 'Adjustment failed');
    } finally {
      setIsAdjustingStudio(false);
    }
  };

  // Agent Details Modal Functions
  const fetchAgentInvitations = async (agentId) => {
    setIsLoadingInvitations(true);
    try {
      const response = await fetch(`${API_URL}/admin/agents/${agentId}/invitations`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAgentInvitations(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching agent invitations:', error);
    } finally {
      setIsLoadingInvitations(false);
    }
  };

  // Resend an invitation (creates a new one with same data)
  const resendInvitation = async (invitation) => {
    setResendingInviteId(invitation._id);
    try {
      const response = await fetch(`${API_URL}/admin/invitations/${invitation._id}/resend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh invitations list
        fetchAgentInvitations(agent._id);
        setSuccess(`Invitation resent to ${invitation.recipientName || invitation.recipientEmail}`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to resend invitation');
        setTimeout(() => setError(''), 3000);
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      setError('Failed to resend invitation');
      setTimeout(() => setError(''), 3000);
    } finally {
      setResendingInviteId(null);
    }
  };

  // QR Code upload function
  const handleQRCodeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setQrError('Please select an image file');
      setTimeout(() => setQrError(''), 3000);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setQrError('Image must be less than 5MB');
      setTimeout(() => setQrError(''), 3000);
      return;
    }

    setIsUploadingQR(true);
    setQrError('');
    setQrSuccess('');

    try {
      // Upload directly to Cloudinary (client-side)
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', 'lauft_profiles'); // Using same preset as profile images
      formData.append('folder', 'lauft/agent-qr-codes');

      console.log('Uploading to Cloudinary...');

      const cloudinaryResponse = await fetch(
        'https://api.cloudinary.com/v1_1/dckv4itgt/image/upload',
        {
          method: 'POST',
          body: formData
        }
      );

      if (!cloudinaryResponse.ok) {
        throw new Error('Failed to upload image to Cloudinary');
      }

      const cloudinaryData = await cloudinaryResponse.json();
      const imageUrl = cloudinaryData.secure_url;

      console.log('Image uploaded to Cloudinary:', imageUrl);

      // Now send the URL to backend
      const response = await fetch(`${API_URL}/admin/agents/${agent._id}/qr-code`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ qrCodeUrl: imageUrl })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setQrSuccess('QR code uploaded successfully!');
        // Update the selected agent's data
        setSelectedAgentForDetails({
          ...selectedAgentForDetails,
          agentInfo: {
            ...selectedAgentForDetails.agentInfo,
            qrCodeUrl: data.data.qrCodeUrl
          }
        });
        // Refresh agents list
        
        setTimeout(() => setQrSuccess(''), 3000);
      } else {
        setQrError(data.error || 'Failed to update QR code');
        setTimeout(() => setQrError(''), 5000);
      }
    } catch (error) {
      console.error('Error uploading QR code:', error);
      setQrError(error.message || 'Failed to upload QR code');
      setTimeout(() => setQrError(''), 5000);
    } finally {
      setIsUploadingQR(false);
      // Reset file input
      if (qrFileInputRef.current) {
        qrFileInputRef.current.value = '';
      }
    }
  };

  // Delete QR Code
  const handleDeleteQRCode = async () => {
    if (!window.confirm('Are you sure you want to delete this QR code?')) return;

    setIsUploadingQR(true);
    setQrError('');

    try {
      const response = await fetch(`${API_URL}/admin/agents/${agent._id}/qr-code`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setQrSuccess('QR code deleted successfully!');
        // Update the selected agent's data
        setSelectedAgentForDetails({
          ...selectedAgentForDetails,
          agentInfo: {
            ...selectedAgentForDetails.agentInfo,
            qrCodeUrl: null
          }
        });
        // Refresh agents list
        
        setTimeout(() => setQrSuccess(''), 3000);
      } else {
        setQrError(data.error || 'Failed to delete QR code');
        setTimeout(() => setQrError(''), 5000);
      }
    } catch (error) {
      console.error('Error deleting QR code:', error);
      setQrError('Failed to delete QR code');
      setTimeout(() => setQrError(''), 5000);
    } finally {
      setIsUploadingQR(false);
    }
  };

  // Delete Agent Function

  /* ---------------- onboarding link ---------------- */

  // Current onboarding status. Never returns the link itself — the token is
  // hashed server-side and is unrecoverable.
  const fetchOnboarding = async (agentId) => {
    setIsLoadingOnboarding(true);
    try {
      const response = await fetch(`${API_URL}/admin/agents/${agentId}/onboarding`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();

      if (response.ok && data.success) {
        setOnboarding(data.data);
      } else {
        setOnboarding(null);
      }
    } catch (err) {
      console.error('Error fetching onboarding status:', err);
      setOnboarding(null);
    } finally {
      setIsLoadingOnboarding(false);
    }
  };

  /**
   * Mint a replacement link. Rotates the token (killing the old link
   * immediately) but PRESERVES any steps the agent already finished.
   *
   * The raw link comes back exactly once, in this response. We hold it in
   * `regeneratedLink` so admin can copy it — e.g. the agent is sitting in a
   * meeting and can't find the email. Once this modal closes it's gone for good
   * and you'd have to regenerate again.
   */
  const regenerateOnboardingLink = async (sendEmail) => {
    setIsRegenerating(true);
    setOnboardingError('');
    setOnboardingSuccess('');
    setRegeneratedLink('');
    setLinkCopied(false);

    try {
      const response = await fetch(
        `${API_URL}/admin/agents/${agent._id}/onboarding/regenerate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${AuthService.getToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ sendEmail })
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setRegeneratedLink(data.data.link);

        if (sendEmail) {
          setOnboardingSuccess(
            data.data.emailSent
              ? `New link emailed to ${agent.email}`
              : `Link created, but the email failed to send (${data.data.emailError}). Copy it below.`
          );
        } else {
          setOnboardingSuccess('New link created. Copy it below — it won\'t be shown again.');
        }

        fetchOnboarding(agent._id);
      } else {
        setOnboardingError(data.error || 'Failed to regenerate link');
      }
    } catch (err) {
      console.error('Error regenerating onboarding link:', err);
      setOnboardingError('Failed to regenerate link');
    } finally {
      setIsRegenerating(false);
    }
  };

  // Kill switch. Doesn't destroy the token — flipping it back on revives the
  // same link, assuming it hasn't expired.
  const toggleOnboardingEnabled = async (enabled) => {
    setIsTogglingOnboarding(true);
    setOnboardingError('');
    setOnboardingSuccess('');

    try {
      const response = await fetch(`${API_URL}/admin/agents/${agent._id}/onboarding`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setOnboardingSuccess(data.message);
        setTimeout(() => setOnboardingSuccess(''), 3000);
        fetchOnboarding(agent._id);
      } else {
        setOnboardingError(data.error || 'Failed to update link');
      }
    } catch (err) {
      console.error('Error toggling onboarding link:', err);
      setOnboardingError('Failed to update link');
    } finally {
      setIsTogglingOnboarding(false);
    }
  };

  const copyOnboardingLink = () => {
    navigator.clipboard.writeText(regeneratedLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }).catch(err => {
      console.error('Clipboard write failed:', err);
      setOnboardingError('Could not copy — select the link and copy manually.');
    });
  };

  /* ---------------- integrations (API keys) ---------------- */

  const fetchIntegrations = async (agentId) => {
    setIsLoadingIntegrations(true);
    setIntegrationsError('');
    try {
      const response = await fetch(`${API_URL}/users/${agentId}/integrations`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setIntegrations(data.integrations || []);
      } else {
        setIntegrations([]);
        setIntegrationsError(data.error || 'Failed to load integrations');
      }
    } catch (err) {
      console.error('Error fetching integrations:', err);
      setIntegrationsError('Failed to load integrations');
    } finally {
      setIsLoadingIntegrations(false);
    }
  };

  const revealIntegrationKey = async (provider) => {
    setRevealingProvider(provider);
    setIntegrationsError('');
    try {
      const response = await fetch(
        `${API_URL}/users/${selectedAgentForDetails._id}/integrations/${encodeURIComponent(provider)}/key`,
        { headers: { 'Authorization': `Bearer ${AuthService.getToken()}` } }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        setRevealedKeys(prev => ({ ...prev, [provider]: { credentials: data.credentials || {}, labels: data.labels || {} } }));
      } else {
        setIntegrationsError(data.error || 'Failed to reveal key');
      }
    } catch (err) {
      console.error('Error revealing key:', err);
      setIntegrationsError('Failed to reveal key');
    } finally {
      setRevealingProvider(null);
    }
  };

  /* ---------------- PM plan + spend cap ---------------- */

  const applyPmBilling = (b) => {
    setPmBilling(b);
    setPmDraftPlan(b?.plan || '');
    setPmDraftCap(b?.monthlySpendCap ?? '');
  };

  const fetchPmBilling = async (agentId) => {
    setIsLoadingPm(true);
    setPmError('');
    try {
      const response = await fetch(`${API_URL}/users/${agentId}/pm-billing`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setPmPlans(data.plans || {});
        applyPmBilling(data.pmBilling);
      } else {
        setPmError(data.error || 'Failed to load plan');
      }
    } catch (err) {
      console.error('Error fetching PM billing:', err);
      setPmError('Failed to load plan');
    } finally {
      setIsLoadingPm(false);
    }
  };

  const savePmBilling = async () => {
    setIsSavingPm(true);
    setPmError('');
    setPmSaved(false);
    try {
      const response = await fetch(`${API_URL}/users/${selectedAgentForDetails._id}/pm-billing`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan: pmDraftPlan || null,
          monthlySpendCap: pmDraftCap === '' ? null : Number(pmDraftCap)
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        applyPmBilling(data.pmBilling);
        setPmSaved(true);
        setTimeout(() => setPmSaved(false), 2000);
      } else {
        setPmError(data.error || 'Failed to save plan');
      }
    } catch (err) {
      console.error('Error saving PM billing:', err);
      setPmError('Failed to save plan');
    } finally {
      setIsSavingPm(false);
    }
  };

  // Live read-only call to the provider with the stored credentials.
  const testIntegration = async (provider) => {
    setTestingProvider(provider);
    setIntegrationsError('');
    try {
      const response = await fetch(
        `${API_URL}/users/${selectedAgentForDetails._id}/integrations/${encodeURIComponent(provider)}/test`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${AuthService.getToken()}` } }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        setTestResults(prev => ({ ...prev, [provider]: data.result }));
      } else {
        setIntegrationsError(data.error || 'Test failed to run');
      }
    } catch (err) {
      console.error('Error testing integration:', err);
      setIntegrationsError('Test failed to run');
    } finally {
      setTestingProvider(null);
    }
  };

  const copyIntegrationKey = (provider, field) => {
    const value = revealedKeys[provider]?.credentials?.[field] || '';
    navigator.clipboard.writeText(value).then(() => {
      setCopiedProvider(`${provider}:${field}`);
      setTimeout(() => setCopiedProvider(null), 2000);
    }).catch(err => {
      console.error('Clipboard write failed:', err);
      setIntegrationsError('Could not copy — select the key and copy manually.');
    });
  };

  /* ---------------- onboarding sessions (the scheduled call) ---------------- */

  const fetchAgentOnboardings = async (agentId) => {
    setIsLoadingSessions(true);
    try {
      const response = await fetch(`${API_URL}/admin/agents/${agentId}/onboardings`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Could not load onboardings');
      setAgentOnboardings(data.data.onboardings || []);
    } catch (err) {
      console.error('Error fetching agent onboardings:', err);
      setAgentOnboardings([]);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  /**
   * Load the pool of onboardings with no account attached.
   *
   * This endpoint RE-RUNS the automatic match server-side before it answers, so
   * opening the picker can itself resolve rows — most unlinked onboardings are
   * not mismatched data, they were just booked before the agent ran checkout.
   * `autoLinkedCount` is why the list sometimes comes back shorter than the
   * badge implied, and it gets said out loud rather than left as a mystery.
   */
  const fetchUnlinkedSessions = async () => {
    setIsLoadingUnlinked(true);
    setSessionError('');
    try {
      const qs = sessionSearch.trim() ? `?search=${encodeURIComponent(sessionSearch.trim())}` : '';
      const response = await fetch(`${API_URL}/admin/onboardings/unlinked${qs}`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Could not load onboardings');

      setUnlinkedSessions(data.data.onboardings || []);

      if (data.data.autoLinkedCount > 0) {
        setSessionNotice(
          `${data.data.autoLinkedCount} onboarding${data.data.autoLinkedCount === 1 ? '' : 's'} ` +
          `matched automatically and ${data.data.autoLinkedCount === 1 ? 'is' : 'are'} no longer listed.`
        );
        // Any of them could have been this agent's.
        fetchAgentOnboardings(agent._id);
      }
    } catch (err) {
      console.error('Error fetching unlinked onboardings:', err);
      setSessionError(err.message || 'Could not load onboardings');
    } finally {
      setIsLoadingUnlinked(false);
    }
  };

  const linkSession = async (onboardingId) => {
    if (linkingId) return;
    setLinkingId(onboardingId);
    setSessionError('');
    setSessionNotice('');
    try {
      const response = await fetch(`${API_URL}/admin/onboardings/${onboardingId}/link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ agentId: agent._id })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Could not link that onboarding');

      setSessionNotice('Linked. Customer service can now action this onboarding.');
      setShowSessionPicker(false);
      setUnlinkedSessions([]);
      setSessionSearch('');
      await fetchAgentOnboardings(agent._id);
    } catch (err) {
      console.error('Error linking onboarding:', err);
      setSessionError(err.message || 'Could not link that onboarding');
    } finally {
      setLinkingId(null);
    }
  };

  /**
   * Detach an account from an onboarding.
   *
   * Worth being honest in the confirm: this hands the row back to the automatic
   * matcher, so a row that was auto-linked on email or phone will simply link
   * itself again. Unlinking is a step on the way to setting the right account,
   * not a way to force a row to stay unattached.
   */
  const unlinkSession = async (onboardingId) => {
    if (linkingId) return;
    const ok = window.confirm(
      'Detach this onboarding from the account?\n\n' +
      'Customer service loses Pools, Unlock, and the access email on this card until ' +
      'it is linked again. If the email or phone still match, it will re-link automatically.'
    );
    if (!ok) return;

    setLinkingId(onboardingId);
    setSessionError('');
    setSessionNotice('');
    try {
      const response = await fetch(`${API_URL}/admin/onboardings/${onboardingId}/link`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Could not unlink');

      setSessionNotice('Detached.');
      await fetchAgentOnboardings(agent._id);
    } catch (err) {
      console.error('Error unlinking onboarding:', err);
      setSessionError(err.message || 'Could not unlink');
    } finally {
      setLinkingId(null);
    }
  };

  const openSessionPicker = () => {
    setShowSessionPicker(true);
    setSessionError('');
    setSessionNotice('');
    fetchUnlinkedSessions();
  };

  /* ---------------- lifecycle ---------------- */

  useEffect(() => {
    if (!agent?._id) return;
    fetchAgentDetailsUsers(agent._id);
    fetchAgentPools(agent._id);
    fetchPoolRequests(agent._id);
    fetchAllPools();
    fetchUserPresets();
    fetchAgentInvitations(agent._id);
    fetchTeamMembers(agent._id);
    fetchAgents();
    fetchReferralLinks(agent._id);
    fetchAgentPriority(agent._id);
    fetchAgentEmailStats(agent._id);
    fetchNotifSettings(agent._id);
    fetchAgentActivity(agent._id, 1, '');
    fetchOnboarding(agent._id);
    fetchAgentOnboardings(agent._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?._id]);

  // A one-shot link belongs to one agent. If the modal is pointed at a different
  // agent, drop it — showing agent A's link on agent B's card would be a bug
  // with real consequences.
  useEffect(() => {
    setRegeneratedLink('');
    setLinkCopied(false);
    setOnboardingError('');
    setOnboardingSuccess('');
    // Same reasoning for the session picker: a list of unlinked onboardings
    // loaded for agent A must not still be on screen offering to link them to
    // agent B.
    setShowSessionPicker(false);
    setUnlinkedSessions([]);
    setSessionSearch('');
    setSessionError('');
    setSessionNotice('');
  }, [agent?._id]);

  // Lazy-load Studio credits when the tab is opened. Not on mount — most
  // accounts in this modal are Pro-only and would 404 for nothing.
  useEffect(() => {
    if (agent?._id && activeTab === 'studio') {
      fetchStudioCredits(agent._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, agent?._id]);

  // Lazy-load integrations when the tab is opened.
  useEffect(() => {
    if (agent?._id && activeTab === 'integrations') {
      fetchIntegrations(agent._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, agent?._id]);

  useEffect(() => {
    if (agent?._id && activeTab === 'pmPlan') {
      fetchPmBilling(agent._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, agent?._id]);

  useEffect(() => {
    setPmBilling(null);
    setPmError('');
    setPmSaved(false);
  }, [agent?._id]);

  // Never carry a revealed key across accounts.
  useEffect(() => {
    setIntegrations([]);
    setRevealedKeys({});
    setTestResults({});
    setIntegrationsError('');
  }, [agent?._id]);

  // Clear the adjustment form and banners when switching accounts, so a reason
  // typed for one agent can't be submitted against the next one.
  useEffect(() => {
    setStudioDelta('');
    setStudioReason('');
    setStudioError('');
    setStudioSuccess('');
    setStudioCredits(null);
  }, [agent?._id]);

  // Refetch the feed from page 1 whenever the type filter changes.
  useEffect(() => {
    if (agent?._id && activeTab === 'activity') {
      fetchAgentActivity(agent._id, 1, activityFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityFilter]);

  // Close the pool / preset dropdowns on outside click.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (poolDropdownRef.current && !poolDropdownRef.current.contains(event.target)) {
        setShowPoolDropdown(false);
      }
      if (presetDropdownRef.current && !presetDropdownRef.current.contains(event.target)) {
        setShowPresetDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!agent) return null;

  const openEditAgentModal = onEdit;
  const openAddPointsModal = onAddPoints;
  const openMessageModal = onMessage;
  const openReferralModal = onReferrals;
  const openResetModal = onResetPassword;
  const openUserDetailsModal = onViewUser;
  const handleRefundUser = onRefundUser;
  const handleSyncAgentListingsForAgent = () => handleSyncAgentListings(agent);


  return (
    <div style={{ ...styles.overlay, left: sidebarOffset }} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* ---- Header ---- */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#111', letterSpacing: '-0.01em' }}>
              Agent Details
            </h3>
            {agentPriority && agentPriority.tier > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '5px 12px', borderRadius: '999px',
                background: 'rgba(37, 99, 235, 0.1)',
                border: '1px solid rgba(37, 99, 235, 0.25)',
                fontSize: '0.75rem', fontWeight: 700, color: '#2563eb',
              }}>
                Priority Tier {agentPriority.tier} · {agentPriority.headStartMinutes}m head start
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => openEditAgentModal(agent)}
              style={{ padding: '8px 14px' }}
            >
              <Edit2 size={16} /> Edit
            </button>
            <button onClick={onClose} style={styles.closeButton}>
              <X size={22} />
            </button>
          </div>
        </div>

        {/* ---- Two columns ---- */}
        <div style={styles.content}>

          {/* LEFT — permanent details + actions */}
          <div style={styles.left}>
              {/* Agent Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '24px' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: hasValidProfileImage(agent) ? 'transparent' : '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  border: '4px solid #f0f0f0'
                }}>
                  {hasValidProfileImage(agent) ? (
                    <img
                      src={agent.profileImage}
                      alt={agent.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  )}
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111', letterSpacing: '-0.01em' }}>
                    {agent.name}
                  </h2>
                  <p style={{ margin: '4px 0 0 0', color: '#9ca3af', fontSize: '12px' }}>
                    {agent.agentInfo?.agency || 'No Agency'}
                  </p>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '3px 10px',
                    background: 'rgba(37, 99, 235, 0.1)',
                    borderRadius: '999px',
                    marginTop: '10px'
                  }}>
                    <Star size={10} color="#1d4ed8" fill="#1d4ed8" />
                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#1d4ed8', letterSpacing: '0.01em' }}>
                      {agent.agentInfo?.plan || 'Launch Advantage'}
                    </span>
                  </div>
                  {teamMembers.length > 0 && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '3px 10px',
                      background: 'rgba(99, 102, 241, 0.1)',
                      borderRadius: '999px',
                      marginTop: '10px',
                      marginLeft: '6px'
                    }}>
                      <Shield size={10} color="#4f46e5" />
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#4f46e5' }}>
                        Team Lead
                      </span>
                    </div>
                  )}
                  {agent.teamLead && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '3px 10px',
                      background: 'rgba(34, 197, 94, 0.1)',
                      borderRadius: '999px',
                      marginTop: '10px',
                      marginLeft: '6px'
                    }}>
                      <Users size={10} color="#16a34a" />
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#16a34a' }}>
                        Team Member
                      </span>
                    </div>
                  )}
                </div>
              </div>


              {/* Stats Grid — 3 per row so the 5 tiles wrap to two rows instead of
                  overflowing the 35%-wide left column and forcing a horizontal scroll. */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                marginBottom: '24px'
              }}>
                <div className="stats-card">
                  <div className="stats-value">
                    {agent.pointsBalance || 0}
                  </div>
                  <div className="stats-label">Points Balance</div>
                </div>
                <div className="stats-card">
                  <div className="stats-value">
                    {agent.assignedUsersCount || 0}
                  </div>
                  <div className="stats-label">Assigned Users</div>
                </div>
                <div className="stats-card">
                  <div className="stats-value">
                    {agent.claimedLeads?.length || 0}
                  </div>
                  <div className="stats-label">Claimed Leads</div>
                </div>
                <div className="stats-card">
                  <div className="stats-value">
                    {referralLinks.length || 0}
                  </div>
                  <div className="stats-label">Referral Links</div>
                </div>
                <div className="stats-card" style={{ 
                  background: teamMembers.length > 0 ? 'rgba(99, 102, 241, 0.1)' : '#fafafa',
                  border: teamMembers.length > 0 ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid #f0f0f0'
                }}>
                  <div className="stats-value" style={{ color: teamMembers.length > 0 ? '#4f46e5' : '#111' }}>
                    {teamMembers.length}
                  </div>
                  <div className="stats-label">Team Members</div>
                </div>
              </div>

              {/* Contact Info */}
              <div style={{
                background: '#fafafa',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px',
                border: '1px solid #f0f0f0'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '600', color: '#111' }}>
                  Contact Information
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Mail size={16} color="#9ca3af" />
                    <div>
                      <span style={{ fontSize: '0.9rem', color: '#111' }}>
                        {agent.agentInfo?.aliasEmail || agent.email}
                      </span>
                      {agent.agentInfo?.aliasEmail && (
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px' }}>
                          Login: {agent.email}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Phone size={16} color="#9ca3af" />
                    <span style={{ fontSize: '0.9rem', color: '#111' }}>
                      {agent.phone || 'No phone'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Briefcase size={16} color="#9ca3af" />
                    <span style={{ fontSize: '0.9rem', color: '#111' }}>
                      {agent.agentInfo?.agency || 'No agency'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <CreditCard size={16} color="#9ca3af" />
                    <span style={{ fontSize: '0.9rem', color: '#111' }}>
                      License: {agent.agentInfo?.licenseNumber || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>


              {/* ---- Actions ---- */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { label: 'Message',        icon: <MessageCircle size={18} />, onClick: () => openMessageModal(agent) },
                  { label: 'Add Points',     icon: <Award size={18} />,         onClick: () => openAddPointsModal(agent) },
                  { label: 'Referrals',      icon: <Link size={18} />,          onClick: () => openReferralModal(agent) },
                  { label: 'Reset Password', icon: <Key size={18} />,           onClick: () => openResetModal(agent) },
                ].map(action => (
                  <button
                    key={action.label}
                    onClick={action.onClick}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      width: '100%',
                      padding: '12px 14px',
                      background: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#111',
                      textAlign: 'left',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f9fafb';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#ffffff';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <span style={{ color: '#6b7280', display: 'flex' }}>{action.icon}</span>
                    <span style={{ flex: 1 }}>{action.label}</span>
                    <ChevronRight size={16} color="#d1d5db" />
                  </button>
                ))}

                <button
                  onClick={() => handleSyncAgentListingsForAgent()}
                  disabled={isSyncingListings}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '12px 14px',
                    background: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    cursor: isSyncingListings ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#111',
                    textAlign: 'left',
                    opacity: isSyncingListings ? 0.6 : 1,
                  }}
                >
                  <span style={{ color: '#6b7280', display: 'flex' }}>
                    <RefreshCw
                      size={18}
                      style={{ animation: isSyncingListings ? 'spin 1s linear infinite' : 'none' }}
                    />
                  </span>
                  <span style={{ flex: 1 }}>
                    {isSyncingListings ? 'Syncing MLS...' : 'Sync MLS Listings'}
                  </span>
                </button>

                <button
                  onClick={() => handleDeleteAgent()}
                  disabled={isDeleting}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '12px 14px',
                    marginTop: '4px',
                    background: 'rgba(239, 68, 68, 0.06)',
                    border: '1px solid rgba(239, 68, 68, 0.25)',
                    borderRadius: '10px',
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#dc2626',
                    textAlign: 'left',
                    opacity: isDeleting ? 0.6 : 1,
                  }}
                >
                  <span style={{ display: 'flex' }}><Trash2 size={18} /></span>
                  <span style={{ flex: 1 }}>{isDeleting ? 'Deleting...' : 'Delete Agent'}</span>
                </button>
              </div>

              {/* ---- Account Setup / Onboarding Link ---- */}
              <div style={{
                background: '#fafafa',
                borderRadius: '12px',
                padding: '16px',
                marginTop: '24px',
                marginBottom: '24px',
                border: '1px solid #f0f0f0'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '600', color: '#111', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Send size={16} />
                  Account Setup
                </h4>

                {isLoadingOnboarding ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
                    Loading...
                  </div>
                ) : !onboarding ? (
                  <div style={{ fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic' }}>
                    No onboarding link on this account.
                  </div>
                ) : (() => {
                  const isComplete = onboarding.status === 'complete';
                  const isDisabled = !onboarding.enabled;
                  const isExpired = onboarding.expired;
                  // Walled out of the portal until admin marks the onboarding
                  // call done. That control lives on the agents list, not here —
                  // this card just has to report the state truthfully.
                  const isLocked = onboarding.locked;
                  // They've finished everything they can and are waiting on us.
                  const awaitingCall = isLocked && onboarding.nextStep === 'onboarding_call';

                  // Status pill: complete wins, then the two failure states,
                  // then awaiting-call vs in-progress vs never-opened.
                  let pill;
                  if (isComplete) {
                    pill = { label: 'Completed', bg: 'rgba(34,197,94,0.1)', fg: '#16a34a', border: 'rgba(34,197,94,0.25)' };
                  } else if (awaitingCall) {
                    pill = { label: 'Awaiting Call', bg: 'rgba(234,88,12,0.1)', fg: '#ea580c', border: 'rgba(234,88,12,0.25)' };
                  } else if (isDisabled) {
                    pill = { label: 'Disabled', bg: 'rgba(107,114,128,0.1)', fg: '#6b7280', border: 'rgba(107,114,128,0.25)' };
                  } else if (isExpired) {
                    pill = { label: 'Expired', bg: 'rgba(239,68,68,0.08)', fg: '#dc2626', border: 'rgba(239,68,68,0.25)' };
                  } else if (onboarding.status === 'in_progress') {
                    pill = { label: 'In Progress', bg: 'rgba(37,99,235,0.1)', fg: '#2563eb', border: 'rgba(37,99,235,0.25)' };
                  } else {
                    pill = { label: 'Not Started', bg: 'rgba(245,158,11,0.1)', fg: '#d97706', border: 'rgba(245,158,11,0.25)' };
                  }

                  const fmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;

                  return (
                    <>
                      {/* Status */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '3px 10px',
                          borderRadius: '999px',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          background: pill.bg,
                          color: pill.fg,
                          border: `1px solid ${pill.border}`
                        }}>
                          {pill.label}
                        </span>
                        {onboarding.completedSteps?.length > 0 && !isComplete && (
                          <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                            {onboarding.completedSteps.length} step{onboarding.completedSteps.length === 1 ? '' : 's'} done
                          </span>
                        )}
                      </div>

                      {/* Meta */}
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', lineHeight: 1.7, marginBottom: '12px' }}>
                        {fmt(onboarding.generatedAt) && (
                          <div>Link sent {fmt(onboarding.generatedAt)}</div>
                        )}
                        {onboarding.lastUsedAt && (
                          <div>Last opened {fmt(onboarding.lastUsedAt)}</div>
                        )}
                        {isComplete && onboarding.completedAt && (
                          <div>Completed {fmt(onboarding.completedAt)}</div>
                        )}
                        {!isComplete && onboarding.expiresAt && (
                          <div style={{ color: isExpired ? '#dc2626' : '#9ca3af' }}>
                            {isExpired ? 'Expired' : 'Expires'} {fmt(onboarding.expiresAt)}
                          </div>
                        )}
                      </div>

                      {onboardingError && (
                        <div style={{
                          padding: '8px 10px',
                          marginBottom: '10px',
                          borderRadius: '8px',
                          background: 'rgba(239,68,68,0.08)',
                          border: '1px solid rgba(239,68,68,0.2)',
                          color: '#dc2626',
                          fontSize: '0.78rem'
                        }}>
                          {onboardingError}
                        </div>
                      )}

                      {onboardingSuccess && (
                        <div style={{
                          padding: '8px 10px',
                          marginBottom: '10px',
                          borderRadius: '8px',
                          background: 'rgba(34,197,94,0.08)',
                          border: '1px solid rgba(34,197,94,0.2)',
                          color: '#16a34a',
                          fontSize: '0.78rem'
                        }}>
                          {onboardingSuccess}
                        </div>
                      )}

                      {/* Show-once link. Gone when this modal closes. */}
                      {regeneratedLink && (
                        <div style={{
                          padding: '10px',
                          marginBottom: '12px',
                          borderRadius: '8px',
                          background: '#ffffff',
                          border: '1px solid #e5e7eb'
                        }}>
                          <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                            Copy now — shown once
                          </div>
                          <div style={{
                            fontSize: '0.72rem',
                            color: '#111',
                            wordBreak: 'break-all',
                            fontFamily: 'monospace',
                            marginBottom: '8px',
                            lineHeight: 1.5
                          }}>
                            {regeneratedLink}
                          </div>
                          <button
                            onClick={copyOnboardingLink}
                            style={{
                              width: '100%',
                              padding: '7px',
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              background: linkCopied ? 'rgba(34,197,94,0.1)' : '#111',
                              color: linkCopied ? '#16a34a' : '#fff',
                              border: linkCopied ? '1px solid rgba(34,197,94,0.25)' : '1px solid #111',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              transition: 'all 0.15s'
                            }}
                          >
                            {linkCopied ? 'Copied' : 'Copy Link'}
                          </button>
                        </div>
                      )}

                      {awaitingCall ? (
                        <div style={{
                          padding: '10px 12px',
                          borderRadius: '8px',
                          background: 'rgba(234,88,12,0.06)',
                          border: '1px solid rgba(234,88,12,0.2)',
                          fontSize: '0.78rem',
                          color: '#9a3412',
                          lineHeight: 1.55
                        }}>
                          Finished every step and is walled out of the portal until
                          their onboarding call. Unlock from the <strong>Locked</strong> badge
                          on the agents list.
                        </div>
                      ) : isComplete ? (
                        <div style={{ fontSize: '0.78rem', color: '#9ca3af', lineHeight: 1.5 }}>
                          This agent has finished setup. Use <strong style={{ color: '#6b7280' }}>Reset Password</strong> above if they need back in.
                        </div>
                      ) : (
                        <>
                          {/* Regenerate. Rotates the token, keeps their progress. */}
                          <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                            <button
                              onClick={() => regenerateOnboardingLink(true)}
                              disabled={isRegenerating || isTogglingOnboarding}
                              style={{
                                flex: 1,
                                padding: '9px',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                background: '#111',
                                color: '#fff',
                                border: '1px solid #111',
                                borderRadius: '8px',
                                cursor: isRegenerating || isTogglingOnboarding ? 'not-allowed' : 'pointer',
                                opacity: isRegenerating || isTogglingOnboarding ? 0.6 : 1
                              }}
                            >
                              {isRegenerating ? 'Working...' : 'New Link + Email'}
                            </button>
                            <button
                              onClick={() => regenerateOnboardingLink(false)}
                              disabled={isRegenerating || isTogglingOnboarding}
                              title="Create a new link without emailing it — copy it and send it yourself"
                              style={{
                                flex: 1,
                                padding: '9px',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                background: '#ffffff',
                                color: '#111',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                cursor: isRegenerating || isTogglingOnboarding ? 'not-allowed' : 'pointer',
                                opacity: isRegenerating || isTogglingOnboarding ? 0.6 : 1
                              }}
                            >
                              {isRegenerating ? 'Working...' : 'New Link Only'}
                            </button>
                          </div>

                          {/* Kill switch */}
                          <button
                            onClick={() => toggleOnboardingEnabled(isDisabled)}
                            disabled={isRegenerating || isTogglingOnboarding}
                            style={{
                              width: '100%',
                              padding: '8px',
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              background: 'transparent',
                              color: isDisabled ? '#16a34a' : '#dc2626',
                              border: `1px solid ${isDisabled ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.3)'}`,
                              borderRadius: '8px',
                              cursor: isRegenerating || isTogglingOnboarding ? 'not-allowed' : 'pointer',
                              opacity: isRegenerating || isTogglingOnboarding ? 0.6 : 1
                            }}
                          >
                            {isTogglingOnboarding
                              ? 'Working...'
                              : isDisabled ? 'Re-enable Link' : 'Disable Link'}
                          </button>

                          <div style={{ fontSize: '0.7rem', color: '#c0c4cb', marginTop: '8px', lineHeight: 1.5 }}>
                            A new link replaces the old one immediately. Progress already made is kept.
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* ---- Onboarding Call (scheduled session) ---- */}
              {/*
                Distinct from Account Setup above. That card is the setup TOKEN;
                this is the scheduled CALL, and the account it points at is what
                customer service needs to run it. An onboarding here with no
                account attached is a dead card on their side.
              */}
              <div style={{
                background: '#fafafa',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px',
                border: '1px solid #f0f0f0'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '600', color: '#111', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Link size={16} />
                  Onboarding Call
                </h4>

                {sessionError && (
                  <div style={{
                    padding: '8px 10px',
                    marginBottom: '10px',
                    borderRadius: '8px',
                    background: 'rgba(239,68,68,0.08)',
                    border: '1px solid rgba(239,68,68,0.2)',
                    color: '#dc2626',
                    fontSize: '0.78rem'
                  }}>
                    {sessionError}
                  </div>
                )}

                {sessionNotice && (
                  <div style={{
                    padding: '8px 10px',
                    marginBottom: '10px',
                    borderRadius: '8px',
                    background: 'rgba(34,197,94,0.08)',
                    border: '1px solid rgba(34,197,94,0.2)',
                    color: '#16a34a',
                    fontSize: '0.78rem'
                  }}>
                    {sessionNotice}
                  </div>
                )}

                {isLoadingSessions ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
                    Loading...
                  </div>
                ) : agentOnboardings.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                    {agentOnboardings.map((session) => {
                      const when = session.scheduledAt ? new Date(session.scheduledAt) : null;
                      const isPast = when && when.getTime() < Date.now();
                      const via = session.linkedAgent?.linkedVia;
                      return (
                        <div key={session._id} style={{
                          padding: '10px 12px',
                          borderRadius: '8px',
                          background: '#ffffff',
                          border: '1px solid #e5e7eb'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#111' }}>
                                {when
                                  ? when.toLocaleString(undefined, {
                                      month: 'short', day: 'numeric', year: 'numeric',
                                      hour: 'numeric', minute: '2-digit'
                                    })
                                  : 'No date'}
                                {isPast && (
                                  <span style={{ marginLeft: '6px', fontSize: '0.7rem', fontWeight: 500, color: '#9ca3af' }}>
                                    past
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '2px', wordBreak: 'break-all' }}>
                                {session.prospectName || '(no name)'}
                                {session.prospectEmail ? ` · ${session.prospectEmail}` : ''}
                              </div>
                              {/* The email the onboarding was booked under, when
                                  it isn't this account's email — that mismatch
                                  IS the reason this section exists, so showing
                                  it explains why the row needed linking. */}
                              {session.prospectEmail &&
                               agent.email &&
                               session.prospectEmail.toLowerCase() !== agent.email.toLowerCase() && (
                                <div style={{ fontSize: '0.7rem', color: '#d97706', marginTop: '3px' }}>
                                  Booked under a different email than this account
                                </div>
                              )}
                              <div style={{ fontSize: '0.7rem', color: '#c0c4cb', marginTop: '3px' }}>
                                {via === 'manual' ? 'Linked by hand' :
                                 via === 'auto_email' ? 'Matched on email' :
                                 via === 'auto_phone' ? 'Matched on phone' : 'Linked'}
                                {session.territory ? ` · ${session.territory}` : ''}
                              </div>
                            </div>
                            <button
                              onClick={() => unlinkSession(session._id)}
                              disabled={!!linkingId}
                              title="Detach this onboarding from this account"
                              style={{
                                flexShrink: 0,
                                padding: '5px 9px',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                background: 'transparent',
                                color: '#dc2626',
                                border: '1px solid rgba(239,68,68,0.3)',
                                borderRadius: '6px',
                                cursor: linkingId ? 'not-allowed' : 'pointer',
                                opacity: linkingId ? 0.6 : 1
                              }}
                            >
                              {linkingId === session._id ? '...' : 'Detach'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{
                    padding: '10px 12px',
                    marginBottom: '10px',
                    borderRadius: '8px',
                    background: 'rgba(234,88,12,0.06)',
                    border: '1px solid rgba(234,88,12,0.2)',
                    fontSize: '0.78rem',
                    color: '#9a3412',
                    lineHeight: 1.55
                  }}>
                    No onboarding call linked to this account. If one was booked, customer
                    service can't run Pools, Unlock, or the access email against it until
                    it's attached here.
                  </div>
                )}

                {!showSessionPicker ? (
                  <button
                    onClick={openSessionPicker}
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      background: agentOnboardings.length ? 'transparent' : '#111',
                      color: agentOnboardings.length ? '#111' : '#fff',
                      border: agentOnboardings.length ? '1px solid #e5e7eb' : '1px solid #111',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    {agentOnboardings.length ? 'Link Another Onboarding' : 'Link an Onboarding'}
                  </button>
                ) : (
                  <div style={{
                    padding: '10px',
                    borderRadius: '8px',
                    background: '#ffffff',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Unattached onboardings
                      </div>
                      <button
                        onClick={() => { setShowSessionPicker(false); setUnlinkedSessions([]); setSessionSearch(''); }}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', padding: 0 }}
                      >
                        <X size={14} />
                      </button>
                    </div>

                    <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                      <input
                        type="text"
                        value={sessionSearch}
                        onChange={(e) => setSessionSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') fetchUnlinkedSessions(); }}
                        placeholder="Search name, email, or market"
                        style={{
                          flex: 1,
                          padding: '7px 9px',
                          fontSize: '0.78rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          outline: 'none'
                        }}
                      />
                      <button
                        onClick={fetchUnlinkedSessions}
                        disabled={isLoadingUnlinked}
                        style={{
                          padding: '7px 11px',
                          fontSize: '0.78rem',
                          fontWeight: 600,
                          background: '#111',
                          color: '#fff',
                          border: '1px solid #111',
                          borderRadius: '6px',
                          cursor: isLoadingUnlinked ? 'not-allowed' : 'pointer',
                          opacity: isLoadingUnlinked ? 0.6 : 1
                        }}
                      >
                        Search
                      </button>
                    </div>

                    {isLoadingUnlinked ? (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '0.8rem' }}>
                        Loading...
                      </div>
                    ) : unlinkedSessions.length === 0 ? (
                      <div style={{ fontSize: '0.78rem', color: '#9ca3af', fontStyle: 'italic', padding: '6px 0' }}>
                        Nothing unattached in the last 90 days.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '260px', overflowY: 'auto' }}>
                        {unlinkedSessions.map((session) => {
                          const when = session.scheduledAt ? new Date(session.scheduledAt) : null;
                          return (
                            <div key={session._id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '10px',
                              padding: '8px 10px',
                              borderRadius: '6px',
                              background: '#fafafa',
                              border: '1px solid #f0f0f0'
                            }}>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#111' }}>
                                  {session.prospectName || '(no name)'}
                                </div>
                                <div style={{ fontSize: '0.72rem', color: '#9ca3af', wordBreak: 'break-all' }}>
                                  {session.prospectEmail || '(no email)'}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#c0c4cb', marginTop: '2px' }}>
                                  {when
                                    ? when.toLocaleString(undefined, {
                                        month: 'short', day: 'numeric',
                                        hour: 'numeric', minute: '2-digit'
                                      })
                                    : 'No date'}
                                  {session.territory ? ` · ${session.territory}` : ''}
                                  {session.demo?.demoName ? ` · ${session.demo.demoName}` : ''}
                                </div>
                              </div>
                              <button
                                onClick={() => linkSession(session._id)}
                                disabled={!!linkingId}
                                style={{
                                  flexShrink: 0,
                                  padding: '6px 11px',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  background: '#111',
                                  color: '#fff',
                                  border: '1px solid #111',
                                  borderRadius: '6px',
                                  cursor: linkingId ? 'not-allowed' : 'pointer',
                                  opacity: linkingId ? 0.6 : 1
                                }}
                              >
                                {linkingId === session._id ? 'Linking...' : 'Link'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div style={{ fontSize: '0.7rem', color: '#c0c4cb', marginTop: '8px', lineHeight: 1.5 }}>
                      Check the prospect name and date against the demo before linking —
                      a wrong link hands customer service an Unlock button for the wrong portal.
                    </div>
                  </div>
                )}
              </div>

              {/* QR Code Management */}
              <div style={{
                background: '#fafafa',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px',
                border: '1px solid #f0f0f0'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '600', color: '#111', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Image size={16} />
                  Stylized QR Code
                </h4>
                
                {qrError && (
                  <div style={{ 
                    background: 'rgba(239, 68, 68, 0.1)', 
                    color: '#dc2626', 
                    padding: '8px 12px', 
                    borderRadius: '8px', 
                    fontSize: '0.85rem',
                    marginBottom: '12px'
                  }}>
                    {qrError}
                  </div>
                )}
                
                {qrSuccess && (
                  <div style={{ 
                    background: 'rgba(34, 197, 94, 0.1)', 
                    color: '#16a34a', 
                    padding: '8px 12px', 
                    borderRadius: '8px', 
                    fontSize: '0.85rem',
                    marginBottom: '12px'
                  }}>
                    {qrSuccess}
                  </div>
                )}

                {agent.agentInfo?.qrCodeUrl ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: '1px solid #f0f0f0',
                      flexShrink: 0
                    }}>
                      <img 
                        src={agent.agentInfo.qrCodeUrl} 
                        alt="Agent QR Code"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6b7280' }}>
                        This QR code is visible to the agent in their portal.
                      </p>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <label style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 14px',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          background: '#ffffff',
                          color: '#111',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          cursor: isUploadingQR ? 'not-allowed' : 'pointer',
                          opacity: isUploadingQR ? 0.6 : 1
                        }}>
                          <Upload size={14} />
                          Replace
                          <input
                            ref={qrFileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleQRCodeUpload}
                            disabled={isUploadingQR}
                            style={{ display: 'none' }}
                          />
                        </label>
                        <button
                          onClick={handleDeleteQRCode}
                          disabled={isUploadingQR}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 14px',
                            borderRadius: '8px',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            background: 'rgba(239, 68, 68, 0.1)',
                            color: '#dc2626',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            cursor: isUploadingQR ? 'not-allowed' : 'pointer',
                            opacity: isUploadingQR ? 0.6 : 1
                          }}
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '24px',
                    border: '2px dashed #e5e7eb',
                    borderRadius: '12px',
                    background: '#ffffff'
                  }}>
                    <Image size={32} color="#d1d5db" style={{ marginBottom: '12px' }} />
                    <p style={{ margin: '0 0 12px 0', fontSize: '0.9rem', color: '#6b7280' }}>
                      No QR code uploaded yet
                    </p>
                    <label style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: '#111',
                      color: '#ffffff',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      cursor: isUploadingQR ? 'not-allowed' : 'pointer',
                      opacity: isUploadingQR ? 0.6 : 1
                    }}>
                      <Upload size={16} />
                      {isUploadingQR ? 'Uploading...' : 'Upload QR Code'}
                      <input
                        ref={qrFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleQRCodeUpload}
                        disabled={isUploadingQR}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <p style={{ margin: '12px 0 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
                      PNG, JPG up to 5MB
                    </p>
                  </div>
                )}
              </div>

        {/* MLS Listings Sync Result Panel — sibling of modal-body */}
            {/* MLS Listings Sync Result Panel */}
            {(syncListingsResult || syncListingsError || isSyncingListings) && (
              <div style={{
                margin: '0 24px 16px 24px',
                padding: '14px 16px',
                borderRadius: '8px',
                background: syncListingsError
                  ? 'rgba(239, 68, 68, 0.08)'
                  : isSyncingListings
                    ? 'rgba(59, 130, 246, 0.08)'
                    : 'rgba(34, 197, 94, 0.08)',
                border: `1px solid ${
                  syncListingsError
                    ? 'rgba(239, 68, 68, 0.3)'
                    : isSyncingListings
                      ? 'rgba(59, 130, 246, 0.3)'
                      : 'rgba(34, 197, 94, 0.3)'
                }`,
                fontSize: '0.9rem'
              }}>
                {isSyncingListings && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#1d4ed8' }}>
                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    <span>Searching MLS for active listings... this can take 30-90 seconds.</span>
                  </div>
                )}
                {syncListingsError && !isSyncingListings && (
                  <div style={{ color: '#b91c1c' }}>
                    <strong>Sync failed:</strong> {syncListingsError}
                  </div>
                )}
                {syncListingsResult && !isSyncingListings && (
                  <div style={{ color: '#15803d' }}>
                    <div style={{ fontWeight: 600, marginBottom: '6px' }}>
                      ✅ Synced {syncListingsResult.found} active listing{syncListingsResult.found === 1 ? '' : 's'} for {syncListingsResult.agentName || 'agent'}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', color: '#374151', fontSize: '0.85rem' }}>
                      <span><strong>{syncListingsResult.created}</strong> new</span>
                      <span><strong>{syncListingsResult.updated}</strong> updated</span>
                      <span><strong>{syncListingsResult.linkedExisting}</strong> newly linked</span>
                      <span><strong>{syncListingsResult.imageRejects}</strong> image-rejects</span>
                      {syncListingsResult.errors > 0 && (
                        <span style={{ color: '#b91c1c' }}><strong>{syncListingsResult.errors}</strong> errors</span>
                      )}
                    </div>
                    {syncListingsResult.matchBreakdown && (
                      <div style={{ marginTop: '6px', color: '#6b7280', fontSize: '0.8rem' }}>
                        Matched by: {syncListingsResult.matchBreakdown.byEmail || 0} email-only,{' '}
                        {syncListingsResult.matchBreakdown.byPhone || 0} phone-only,{' '}
                        {syncListingsResult.matchBreakdown.byMlsCode || 0} mlsCode-only,{' '}
                        {syncListingsResult.matchBreakdown.multi || 0} multi-match
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT — tabbed detail */}
          <div style={styles.right}>

              {/* ---- Tabs ---- */}
              <div style={{
                display: 'flex',
                gap: '4px',
                borderBottom: '1px solid #e5e7eb',
                marginBottom: '20px',
                overflowX: 'auto',
                flexShrink: 0,
              }}>
                {[
                  {
                    key: 'territories',
                    // Pending count on the tab — otherwise requests sit unseen
                    // behind a tab labelled with a number that hasn't changed.
                    label: pendingRequestCount
                      ? `Territories (${agentPools.length}) · ${pendingRequestCount} pending`
                      : `Territories (${agentPools.length})`
                  },
                  { key: 'users',       label: `Users (${agentDetailsUsers.length})` },
                  { key: 'invitations', label: `Invitations (${agentInvitations.length})` },
                  { key: 'activity',    label: 'Activity' },
                  { key: 'notifications', label: 'Notifications' },
                  { key: 'integrations', label: 'Integrations' },
                  // Studio-only. Hidden for Pro-only accounts rather than shown
                  // empty — the endpoints 400 NOT_STUDIO for them anyway, and a
                  // visible tab invites granting credits nothing can spend.
                  // Lease-only — PM plan and monthly spend cap.
                  ...(selectedAgentForDetails?.entitlements?.includes('lease')
                    ? [{ key: 'pmPlan', label: 'PM Plan' }]
                    : []),
                  ...(selectedAgentForDetails?.entitlements?.includes('studio')
                    ? [{ key: 'studio', label: 'Studio' }]
                    : []),
                ].map(tab => (
                  <div
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      padding: '10px 18px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      userSelect: 'none',
                      color: activeTab === tab.key ? '#111' : '#9ca3af',
                      borderBottom: activeTab === tab.key ? '2px solid #111' : '2px solid transparent',
                      marginBottom: '-1px',
                      transition: 'color 0.2s',
                    }}
                  >
                    {tab.label}
                  </div>
                ))}
              </div>

            {activeTab === 'territories' && (<>
              {/* Territories / Agent Pools */}
              <div style={{
                background: '#fafafa',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px',
                border: '1px solid #f0f0f0'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '600', color: '#111', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MapPin size={16} />
                  Territories ({agentPools.length})
                </h4>
                
                {/* ---- Requested during onboarding, awaiting approval ---- */}
                {(() => {
                  const pending = poolRequests.filter(r => r.status === 'pending');
                  if (isLoadingRequests || (!pending.length && !poolsDeferred && !requestError)) return null;

                  // Collapse a county into one row. A 23-pool preset shown as 23
                  // approve buttons is how someone gives up and approves nothing.
                  const groups = [];
                  const byPreset = new Map();
                  for (const request of pending) {
                    if (request.viaPreset) {
                      const key = String(request.viaPreset._id || request.viaPreset);
                      if (!byPreset.has(key)) {
                        const group = { key, label: request.viaPreset.name || 'County', requests: [] };
                        byPreset.set(key, group);
                        groups.push(group);
                      }
                      byPreset.get(key).requests.push(request);
                    } else {
                      groups.push({
                        key: String(request._id),
                        label: request.pool?.name || 'Unknown pool',
                        requests: [request],
                        single: true
                      });
                    }
                  }

                  const busy = !!decidingKey;

                  return (
                    <div style={{
                      padding: '12px',
                      marginBottom: '14px',
                      borderRadius: '10px',
                      border: '1px solid rgba(234,88,12,0.22)',
                      background: 'rgba(234,88,12,0.05)'
                    }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#9a3412', marginBottom: '8px' }}>
                        {pending.length
                          ? `Review requests (${pending.length})`
                          : 'Review requests'}
                      </div>

                      {requestError && (
                        <div style={{ fontSize: '0.75rem', color: '#dc2626', marginBottom: '8px' }}>
                          {requestError}
                        </div>
                      )}

                      {poolsDeferred && (
                        <div style={{ fontSize: '0.75rem', color: '#9a3412', lineHeight: 1.55, marginBottom: pending.length ? '10px' : 0 }}>
                          <strong>No areas were offered to this agent.</strong> Their territory
                          was unset or mapped to no market when they reached that step, so they
                          passed an empty list and won't see it again. Set their Territory, then
                          add pools below by hand.
                        </div>
                      )}

                      {pending.length > 0 && (
                        <div style={{ fontSize: '0.72rem', color: '#9a3412', marginBottom: '9px' }}>
                          Approving adds them to the pool — that's when lead emails start.
                        </div>
                      )}

                      {groups.map(group => (
                        <div
                          key={group.key}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 10px',
                            marginBottom: '6px',
                            borderRadius: '8px',
                            border: '1px solid #f0f0f0',
                            background: '#ffffff'
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: '600', color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {group.label}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                              {group.single
                                ? (group.requests[0].pool?.serviceArea?.city
                                    ? `${group.requests[0].pool.serviceArea.city}, ${group.requests[0].pool.serviceArea.state || ''}`
                                    : 'Individual pool')
                                : `${group.requests.length} ${group.requests.length === 1 ? 'pool' : 'pools'}`}
                            </div>
                          </div>

                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => decideRequests(group.requests, 'denied', group.key)}
                            style={{
                              padding: '5px 10px', borderRadius: '7px', border: '1px solid #e5e7eb',
                              background: '#ffffff', color: '#6b7280', fontSize: '0.75rem',
                              fontWeight: '600', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1
                            }}
                          >
                            {decidingKey === group.key ? '…' : 'Deny'}
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => decideRequests(group.requests, 'approved', group.key)}
                            style={{
                              padding: '5px 10px', borderRadius: '7px', border: '1px solid #2563eb',
                              background: '#2563eb', color: '#ffffff', fontSize: '0.75rem',
                              fontWeight: '600', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1
                            }}
                          >
                            {decidingKey === group.key ? 'Saving…' : 'Approve'}
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {isLoadingPools ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af' }}>
                    Loading territories...
                  </div>
                ) : (
                  <>
                    {/* Current Pools */}
                    {agentPools.length > 0 && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {agentPools.map(pool => (
                            <div
                              key={pool._id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '6px 10px',
                                background: 'rgba(37, 99, 235, 0.1)',
                                border: '1px solid rgba(37, 99, 235, 0.2)',
                                borderRadius: '8px',
                                fontSize: '0.85rem',
                                color: '#111'
                              }}
                            >
                              <span style={{ fontWeight: '500' }}>
                                {pool.serviceArea?.city && pool.serviceArea?.state 
                                  ? `${pool.serviceArea.city}, ${pool.serviceArea.state}`
                                  : pool.name}
                              </span>
                              <button
                                onClick={() => removeAgentFromPool(pool._id)}
                                disabled={isUpdatingPool}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: '2px',
                                  cursor: isUpdatingPool ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  opacity: isUpdatingPool ? 0.5 : 1
                                }}
                                title="Remove from territory"
                              >
                                <X size={14} color="#111" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Presets — searchable, mirrors the pool search below */}
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'rgba(26,26,26,0.5)', fontWeight: '500', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Presets
                      </div>

                      {userPresets.length === 0 ? (
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic', padding: '4px 0' }}>
                          No presets yet. Create them in Pool Management &rarr; Pool Presets.
                        </div>
                      ) : (() => {
                        const filteredPresets = userPresets
                          .filter(preset => {
                            const searchLower = presetSearchTerm.toLowerCase();
                            const name = preset.name || '';
                            const description = preset.description || '';
                            return name.toLowerCase().includes(searchLower) ||
                                   description.toLowerCase().includes(searchLower);
                          })
                          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

                        return (
                          <div ref={presetDropdownRef} style={{ position: 'relative' }}>
                            <input
                              type="text"
                              value={presetSearchTerm}
                              onChange={(e) => {
                                setPresetSearchTerm(e.target.value);
                                setShowPresetDropdown(true);
                              }}
                              onFocus={() => setShowPresetDropdown(true)}
                              placeholder="+ Apply preset... (type to search)"
                              disabled={isApplyingPreset || isUpdatingPool}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                background: '#ffffff',
                                fontSize: '0.85rem',
                                color: '#111',
                                cursor: isApplyingPreset || isUpdatingPool ? 'not-allowed' : 'text',
                                opacity: isApplyingPreset || isUpdatingPool ? 0.6 : 1
                              }}
                            />

                            {/* Dropdown Results */}
                            {showPresetDropdown && filteredPresets.length > 0 && (
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                marginTop: '4px',
                                background: '#ffffff',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                zIndex: 1001
                              }}>
                                {filteredPresets.map(preset => (
                                  <div
                                    key={preset._id}
                                    onClick={() => {
                                      applyPreset(preset);
                                      setPresetSearchTerm('');
                                      setShowPresetDropdown(false);
                                    }}
                                    title={preset.description || ''}
                                    style={{
                                      padding: '10px 12px',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      color: '#111',
                                      borderBottom: '1px solid #f0f0f0',
                                      transition: 'background 0.15s ease',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: '8px'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                                  >
                                    <span>{preset.name}</span>
                                    <span style={{ fontSize: '0.75rem', color: '#9ca3af', flexShrink: 0 }}>
                                      {preset.pools?.length || 0} pools
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* No Results Message */}
                            {showPresetDropdown && presetSearchTerm && filteredPresets.length === 0 && (
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                marginTop: '4px',
                                background: '#ffffff',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                padding: '12px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                zIndex: 1001,
                                fontSize: '0.85rem',
                                color: '#9ca3af',
                                textAlign: 'center'
                              }}>
                                No presets found matching "{presetSearchTerm}"
                              </div>
                            )}
                          </div>
                        );
                      })()}


                      {/* Missing pools notice */}
                      {presetMissingPools.length > 0 && (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px 10px',
                          backgroundColor: 'rgba(251,191,36,0.1)',
                          border: '1px solid rgba(251,191,36,0.3)',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          color: 'rgba(26,26,26,0.7)',
                          lineHeight: '1.5'
                        }}>
                          <span style={{ fontWeight: '600' }}>Not found in DB ({presetMissingPools.length}):</span>{' '}
                          {presetMissingPools.join(', ')}
                        </div>
                      )}
                    </div>

                    {/* Add to Pool Searchable Dropdown */}
                    {(() => {
                      const availablePools = allPools.filter(
                        pool => !agentPools.some(ap => ap._id === pool._id)
                      );
                      
                      if (availablePools.length === 0 && agentPools.length === 0) {
                        return (
                          <div style={{ 
                            padding: '12px', 
                            textAlign: 'center', 
                            color: '#9ca3af',
                            fontSize: '0.85rem'
                          }}>
                            No territories available. Create pools first.
                          </div>
                        );
                      }
                      
                      if (availablePools.length === 0) {
                        return null;
                      }
                      
                      // Filter pools based on search term
                      const filteredPools = availablePools
                        .filter(pool => {
                          const searchLower = poolSearchTerm.toLowerCase();
                          const poolName = pool.serviceArea?.city || pool.name;
                          const poolState = pool.serviceArea?.state || '';
                          return poolName.toLowerCase().includes(searchLower) || 
                                 poolState.toLowerCase().includes(searchLower);
                        })
                        .sort((a, b) => {
                          const aName = a.serviceArea?.city || a.name;
                          const bName = b.serviceArea?.city || b.name;
                          return aName.localeCompare(bName);
                        });
                      
                      return (
                        <div ref={poolDropdownRef} style={{ position: 'relative' }}>
                          <div style={{ fontSize: '0.75rem', color: 'rgba(26,26,26,0.5)', fontWeight: '500', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Single
                          </div>
                          <input
                            type="text"
                            value={poolSearchTerm}
                            onChange={(e) => {
                              setPoolSearchTerm(e.target.value);
                              setShowPoolDropdown(true);
                            }}
                            onFocus={() => setShowPoolDropdown(true)}
                            placeholder="+ Add to territory... (type to search)"
                            disabled={isUpdatingPool}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: '1px solid #e5e7eb',
                              background: '#ffffff',
                              fontSize: '0.85rem',
                              color: '#111',
                              cursor: isUpdatingPool ? 'not-allowed' : 'text'
                            }}
                          />
                          
                          {/* Dropdown Results */}
                          {showPoolDropdown && filteredPools.length > 0 && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              marginTop: '4px',
                              background: '#ffffff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              maxHeight: '200px',
                              overflowY: 'auto',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                              zIndex: 1000
                            }}>
                              {filteredPools.map(pool => (
                                <div
                                  key={pool._id}
                                  onClick={() => {
                                    addAgentToPool(pool._id);
                                    setPoolSearchTerm('');
                                    setShowPoolDropdown(false);
                                  }}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    color: '#111',
                                    borderBottom: '1px solid #f0f0f0',
                                    transition: 'background 0.15s ease'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                                >
                                  {pool.serviceArea?.city && pool.serviceArea?.state 
                                    ? `${pool.serviceArea.city}, ${pool.serviceArea.state}`
                                    : pool.name}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* No Results Message */}
                          {showPoolDropdown && poolSearchTerm && filteredPools.length === 0 && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              marginTop: '4px',
                              background: '#ffffff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              padding: '12px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                              zIndex: 1000,
                              fontSize: '0.85rem',
                              color: '#9ca3af',
                              textAlign: 'center'
                            }}>
                              No territories found matching "{poolSearchTerm}"
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Remove All — sits under both search bars, acts on the agent's pools */}
                    <button
                      onClick={removeAllPools}
                      disabled={agentPools.length === 0 || isApplyingPreset || isUpdatingPool}
                      style={{
                        marginTop: '12px',
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        backgroundColor: 'transparent',
                        color: '#dc3545',
                        border: '1px solid rgba(220,53,69,0.4)',
                        borderRadius: '8px',
                        cursor: agentPools.length === 0 || isApplyingPreset || isUpdatingPool ? 'not-allowed' : 'pointer',
                        opacity: agentPools.length === 0 || isApplyingPreset || isUpdatingPool ? 0.5 : 1,
                        transition: 'all 0.15s'
                      }}
                      onMouseEnter={(e) => { if (agentPools.length > 0 && !isApplyingPreset && !isUpdatingPool) { e.currentTarget.style.backgroundColor = 'rgba(220,53,69,0.08)'; }}}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {isApplyingPreset ? 'Removing...' : `Remove All (${agentPools.length})`}
                    </button>
                  </>
                )}
              </div>

              {/* Team Lead Management */}
              <div style={{
                background: '#fafafa',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px',
                border: '1px solid #f0f0f0'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '600', color: '#111', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Shield size={16} />
                  Team Management
                </h4>
                
                {teamLeadError && (
                  <div style={{ 
                    background: 'rgba(239, 68, 68, 0.1)', 
                    color: '#dc2626', 
                    padding: '8px 12px', 
                    borderRadius: '8px', 
                    fontSize: '0.85rem',
                    marginBottom: '12px'
                  }}>
                    {teamLeadError}
                  </div>
                )}
                
                {teamLeadSuccess && (
                  <div style={{ 
                    background: 'rgba(34, 197, 94, 0.1)', 
                    color: '#16a34a', 
                    padding: '8px 12px', 
                    borderRadius: '8px', 
                    fontSize: '0.85rem',
                    marginBottom: '12px'
                  }}>
                    {teamLeadSuccess}
                  </div>
                )}

                {/* Assign to Team Lead */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.8rem', 
                    fontWeight: '500', 
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Reports To (Team Lead)
                  </label>
                  <select
                    value={agent.teamLead?._id || agent.teamLead || ''}
                    onChange={(e) => assignTeamLead(agent._id, e.target.value || null)}
                    disabled={isUpdatingTeamLead || teamMembers.length > 0}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      background: teamMembers.length > 0 ? '#f0f0f0' : '#ffffff',
                      fontSize: '0.9rem',
                      color: '#111',
                      cursor: (isUpdatingTeamLead || teamMembers.length > 0) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">No Team Lead (Independent)</option>
                    {agents
                      .filter(a => 
                        a._id !== agent._id && // Can't be own team lead
                        !a.teamLead // Can't assign someone who has a team lead as a team lead
                      )
                      .map(a => (
                        <option key={a._id} value={a._id}>
                          {a.name} {a.agentInfo?.agency ? `(${a.agentInfo.agency})` : ''}
                        </option>
                      ))
                    }
                  </select>
                  {teamMembers.length > 0 && (
                    <small style={{ 
                      display: 'block', 
                      marginTop: '6px', 
                      color: '#9ca3af',
                      fontSize: '0.75rem'
                    }}>
                      Cannot assign a team lead because this agent has team members. Remove their team members first.
                    </small>
                  )}
                </div>

                {/* Team Members (if this agent is a team lead) */}
                {isLoadingTeamMembers ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af' }}>
                    Loading team members...
                  </div>
                ) : teamMembers.length > 0 ? (
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '0.8rem', 
                      fontWeight: '500', 
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      Team Members ({teamMembers.length})
                    </label>
                    <div style={{ 
                      background: '#ffffff', 
                      borderRadius: '8px', 
                      border: '1px solid #f0f0f0',
                      maxHeight: '150px',
                      overflowY: 'auto'
                    }}>
                      {teamMembers.map(member => (
                        <div
                          key={member._id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 12px',
                            borderBottom: '1px solid #fafafa'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              background: 'rgba(99, 102, 241, 0.15)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid rgba(99, 102, 241, 0.3)'
                            }}>
                              <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#4f46e5' }}>
                                {member.name?.charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>
                            <div>
                              <div style={{ fontWeight: '500', fontSize: '0.9rem', color: '#111' }}>
                                {member.name}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                {member.pointsBalance || 0} pts • {member.claimedLeads?.length || 0} claimed
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              assignTeamLead(member._id, null);
                              // Refresh team members after removing
                              setTimeout(() => fetchTeamMembers(agent._id), 500);
                            }}
                            disabled={isUpdatingTeamLead}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: '4px',
                              cursor: isUpdatingTeamLead ? 'not-allowed' : 'pointer',
                              opacity: isUpdatingTeamLead ? 0.5 : 1
                            }}
                            title="Remove from team"
                          >
                            <X size={16} color="#9ca3af" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ 
                    padding: '12px', 
                    textAlign: 'center', 
                    color: '#9ca3af',
                    fontSize: '0.85rem',
                    background: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #f0f0f0'
                  }}>
                    {agent.teamLead 
                      ? 'This agent is a team member (not a team lead)'
                      : 'No team members. Assign other agents to this team lead to build a team.'
                    }
                  </div>
                )}
              </div>

            </>)}
            {activeTab === 'users' && (<>
              {/* Assigned Users */}
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '600', color: '#111' }}>
                  Assigned Users ({agentDetailsUsers.length})
                </h4>
                {refundSuccess && (
                  <div style={{ 
                    padding: '10px 14px', 
                    marginBottom: '12px', 
                    borderRadius: '8px', 
                    background: 'rgba(34, 197, 94, 0.1)', 
                    color: '#16a34a', 
                    fontSize: '0.85rem',
                    border: '1px solid rgba(34, 197, 94, 0.2)'
                  }}>
                    {refundSuccess}
                  </div>
                )}
                {refundError && (
                  <div style={{ 
                    padding: '10px 14px', 
                    marginBottom: '12px', 
                    borderRadius: '8px', 
                    background: 'rgba(239, 68, 68, 0.1)', 
                    color: '#dc2626', 
                    fontSize: '0.85rem',
                    border: '1px solid rgba(239, 68, 68, 0.2)'
                  }}>
                    {refundError}
                  </div>
                )}
                {isLoadingAgentDetails ? (
                  <div className="loading-spinner">Loading users...</div>
                ) : agentDetailsUsers.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '24px', 
                    background: '#fafafa', 
                    borderRadius: '12px',
                    color: '#9ca3af',
                    border: '1px solid #f0f0f0'
                  }}>
                    No assigned users yet
                  </div>
                ) : (
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {agentDetailsUsers.map(user => (
                      <div
                        key={user._id}
                        className="user-list-item"
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        <div 
                          style={{ display: 'flex', alignItems: 'center', flex: 1, cursor: 'pointer' }}
                          onClick={() => openUserDetailsModal(user)}
                        >
                          <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            background: 'rgba(37, 99, 235, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '12px',
                            border: '1px solid rgba(37, 99, 235, 0.2)'
                          }}>
                            <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#111' }}>
                              {user.name?.charAt(0).toUpperCase() || '?'}
                            </span>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '500', color: '#111' }}>{user.name}</div>
                            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                              {user.email}
                              {user.leadTier && (
                                <span style={{
                                  marginLeft: '8px',
                                  padding: '1px 6px',
                                  borderRadius: '4px',
                                  fontSize: '0.7rem',
                                  fontWeight: '600',
                                  textTransform: 'capitalize',
                                  background: tierStyle(user.leadTier).bg,
                                  color: tierStyle(user.leadTier).fg
                                }}>
                                  {user.leadTier}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRefundUser(user);
                          }}
                          disabled={refundingUserId === user._id}
                          title="Refund user — returns points to agent and unassigns"
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            background: refundingUserId === user._id ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                            color: '#dc2626',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            cursor: refundingUserId === user._id ? 'not-allowed' : 'pointer',
                            marginRight: '8px',
                            whiteSpace: 'nowrap',
                            opacity: refundingUserId === user._id ? 0.6 : 1,
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (refundingUserId !== user._id) {
                              e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (refundingUserId !== user._id) {
                              e.target.style.background = 'transparent';
                            }
                          }}
                        >
                          {refundingUserId === user._id ? (
                            <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            'Refund'
                          )}
                        </button>
                        <ChevronRight 
                          size={16} 
                          color="#9ca3af" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => openUserDetailsModal(user)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </>)}
            {activeTab === 'invitations' && (<>
              {/* Agent Invitations */}
              <div style={{
                background: '#fafafa',
                borderRadius: '12px',
                padding: '16px',
                marginTop: '24px',
                border: '1px solid #f0f0f0'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '600', color: '#111', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Send size={16} />
                  Sent Invitations ({agentInvitations.length})
                </h4>
                {isLoadingInvitations ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af' }}>
                    Loading invitations...
                  </div>
                ) : agentInvitations.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '24px', 
                    color: '#9ca3af',
                    fontSize: '0.9rem'
                  }}>
                    No invitations sent yet
                  </div>
                ) : (
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {agentInvitations.map(invitation => (
                      <div
                        key={invitation._id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px',
                          background: '#ffffff',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          border: '1px solid #f0f0f0'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', color: '#111', fontSize: '0.9rem' }}>
                            {invitation.recipientName || 'No name'}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                            {invitation.recipientEmail || invitation.recipientPhone}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px' }}>
                            {new Date(invitation.invitedAt).toLocaleDateString()} • 
                            <span style={{
                              marginLeft: '4px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              fontWeight: '500',
                              background: invitation.status === 'accepted' 
                                ? 'rgba(34, 197, 94, 0.15)' 
                                : invitation.status === 'sent'
                                ? 'rgba(59, 130, 246, 0.15)'
                                : 'rgba(251, 191, 36, 0.15)',
                              color: invitation.status === 'accepted'
                                ? '#16a34a'
                                : invitation.status === 'sent'
                                ? '#2563eb'
                                : '#d97706'
                            }}>
                              {invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)}
                            </span>
                          </div>
                        </div>
                        {invitation.status !== 'accepted' && invitation.recipientEmail && (
                          <button
                            onClick={() => resendInvitation(invitation)}
                            disabled={resendingInviteId === invitation._id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb',
                              background: '#ffffff',
                              color: '#111',
                              fontSize: '0.8rem',
                              fontWeight: '500',
                              cursor: resendingInviteId === invitation._id ? 'not-allowed' : 'pointer',
                              opacity: resendingInviteId === invitation._id ? 0.6 : 1
                            }}
                          >
                            <RefreshCw size={14} style={{ 
                              animation: resendingInviteId === invitation._id ? 'spin 1s linear infinite' : 'none' 
                            }} />
                            {resendingInviteId === invitation._id ? 'Sending...' : 'Resend'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>)}
              {/* ---- Tab: Activity (ADMIN ONLY) ---- */}
              {activeTab === 'activity' && (
                <div>
                  {/* Priority state. Agents never see this — showing an agent
                      their own tier would just teach them to game the queue. */}
                  {agentPriority && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                      gap: '12px',
                      marginBottom: '24px'
                    }}>
                      <div style={{
                        padding: '14px',
                        background: agentPriority.tier > 0 ? 'rgba(37, 99, 235, 0.06)' : '#f9fafb',
                        border: `1px solid ${agentPriority.tier > 0 ? 'rgba(37, 99, 235, 0.2)' : '#e5e7eb'}`,
                        borderRadius: '10px'
                      }}>
                        <div style={{
                          fontSize: '1.5rem',
                          fontWeight: 700,
                          color: agentPriority.tier > 0 ? '#2563eb' : '#111'
                        }}>
                          {agentPriority.tier}
                        </div>
                        <div className="stats-label">Priority Tier</div>
                        {agentPriority.tier > 0 && (
                          <div style={{ fontSize: '11px', color: '#2563eb', marginTop: '2px' }}>
                            {agentPriority.headStartMinutes}m head start
                          </div>
                        )}
                      </div>

                      <div style={{ padding: '14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111' }}>
                          {agentPriority.last30Days.attempted}
                        </div>
                        <div className="stats-label">Claims Tried (30d)</div>
                        {agentPriority.last30Days.attemptRate != null && (
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                            {agentPriority.last30Days.attemptRate}% of notified
                          </div>
                        )}
                      </div>

                      <div style={{ padding: '14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#dc2626' }}>
                          {agentPriority.last30Days.failed}
                        </div>
                        <div className="stats-label">Beaten To It (30d)</div>
                        {agentPriority.last30Days.lossRate != null && (
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                            {agentPriority.last30Days.lossRate}% loss rate
                          </div>
                        )}
                      </div>

                      <div style={{ padding: '14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#16a34a' }}>
                          {agentPriority.last30Days.claimed}
                        </div>
                        <div className="stats-label">Claimed (30d)</div>
                        {agentPriority.last30Days.medianResponseSeconds != null && (
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                            {formatResponseTime(agentPriority.last30Days.medianResponseSeconds)} median
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Lead-alert email engagement. Sends are complete history;
                      opens/clicks only exist from the day the SendGrid event
                      webhook went live. */}
                  {emailStats && (
                    <div style={{ marginBottom: '24px' }}>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        color: '#9ca3af',
                        marginBottom: '10px'
                      }}>
                        Lead Alert Emails
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                        gap: '12px'
                      }}>
                        <div style={{ padding: '14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111' }}>
                            {emailStats.emailsReceived}
                          </div>
                          <div className="stats-label">Emails Sent (30d)</div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                            {emailStats.emailsReceivedAllTime} all time
                          </div>
                        </div>

                        {/* Click rate leads because it's the trustworthy one. */}
                        <div style={{
                          padding: '14px',
                          background: emailStats.clicked > 0 ? 'rgba(22, 163, 74, 0.06)' : '#f9fafb',
                          border: `1px solid ${emailStats.clicked > 0 ? 'rgba(22, 163, 74, 0.2)' : '#e5e7eb'}`,
                          borderRadius: '10px'
                        }}>
                          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: emailStats.clicked > 0 ? '#16a34a' : '#111' }}>
                            {emailStats.clickRate != null ? `${emailStats.clickRate}%` : '—'}
                          </div>
                          <div className="stats-label">Click Rate</div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                            {emailStats.clicked} of {emailStats.delivered} delivered
                          </div>
                        </div>

                        {/* Greyed on purpose — Apple Mail Privacy Protection
                            pre-fetches the pixel on delivery, so this fires
                            whether or not a human looked. Never evaluate an
                            agent on it. */}
                        <div style={{ padding: '14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#6b7280' }}>
                            {emailStats.openRate != null ? `${emailStats.openRate}%` : '—'}
                          </div>
                          <div className="stats-label">Open Rate</div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                            unreliable · Apple MPP
                          </div>
                        </div>

                        {/* A bounce or spam report silently suppresses future
                            delivery — the agent just stops getting Users with
                            no other signal anywhere in the system. */}
                        <div style={{
                          padding: '14px',
                          background: (emailStats.bounced + emailStats.spamReports) > 0 ? 'rgba(220, 38, 38, 0.06)' : '#f9fafb',
                          border: `1px solid ${(emailStats.bounced + emailStats.spamReports) > 0 ? 'rgba(220, 38, 38, 0.2)' : '#e5e7eb'}`,
                          borderRadius: '10px'
                        }}>
                          <div style={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: (emailStats.bounced + emailStats.spamReports) > 0 ? '#dc2626' : '#111'
                          }}>
                            {emailStats.bounced + emailStats.spamReports}
                          </div>
                          <div className="stats-label">Delivery Issues</div>
                          {(emailStats.bounced + emailStats.spamReports) > 0 && (
                            <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px' }}>
                              may be suppressed
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Without this an untracked agent reads as disengaged. */}
                      {!emailStats.trackingSince && (
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
                          No open/click tracking recorded yet for this agent.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Feed filter */}
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
                    {ACTIVITY_FILTERS.map(f => (
                      <button
                        key={f.key || 'all'}
                        onClick={() => setActivityFilter(f.key)}
                        style={{
                          padding: '5px 12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          borderRadius: '999px',
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: activityFilter === f.key ? '#111' : '#e5e7eb',
                          background: activityFilter === f.key ? '#111' : '#ffffff',
                          color: activityFilter === f.key ? '#ffffff' : '#6b7280'
                        }}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>

                  {isLoadingActivity ? (
                    <div className="loading-spinner">Loading activity...</div>
                  ) : agentActivity.length === 0 ? (
                    <div className="empty-state">
                      No activity yet. Rows appear here once this agent is notified
                      about a User or attempts a claim.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {agentActivity.map(item => {
                        const meta = ACTIVITY_META[item.type] || { Icon: Circle, color: '#6b7280' };
                        const ActivityIcon = meta.Icon;
                        return (
                          <div
                            key={item._id}
                            style={{
                              display: 'flex',
                              gap: '12px',
                              padding: '12px 14px',
                              borderRadius: '8px',
                              borderLeft: `3px solid ${meta.color}`,
                              background: item.type === 'claim_failed' && item.withinGrievanceWindow
                                ? 'rgba(220, 38, 38, 0.04)'
                                : 'transparent'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', height: '20px', flexShrink: 0 }}>
                              <ActivityIcon size={15} color={meta.color} strokeWidth={2} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '13px', color: '#111', fontWeight: 500 }}>
                                {item.message}
                              </div>
                              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>
                                {new Date(item.createdAt).toLocaleString()}
                                {item.lead?.name ? ` · ${item.lead.name}` : ''}
                                {item.pool?.name ? ` · ${item.pool.name}` : ''}
                              </div>
                            </div>
                            {item.leadTier && (
                              <span style={{
                                alignSelf: 'flex-start',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '10px',
                                fontWeight: 600,
                                textTransform: 'capitalize',
                                background: tierStyle(item.leadTier).bg,
                                color: tierStyle(item.leadTier).fg,
                                whiteSpace: 'nowrap'
                              }}>
                                {item.leadTier}
                              </span>
                            )}
                          </div>
                        );
                      })}

                      {activityHasMore && (
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ marginTop: '12px', alignSelf: 'center' }}
                          onClick={() => fetchAgentActivity(agent._id, activityPage + 1, activityFilter, true)}
                        >
                          Load more
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'notifications' && notifSettings && (
                <div style={{ padding: '4px 0' }}>

                  {notifError && (
                    <div style={{
                      padding: '10px 12px', marginBottom: '14px',
                      background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.25)',
                      borderRadius: '8px', fontSize: '13px', color: '#b91c1c'
                    }}>
                      {notifError}
                    </div>
                  )}

                  {/* Silenced entirely — an agent in this state hears nothing
                      and will only notice by missing Users. */}
                  {notifSettings.noAlertChannelsRemaining && (
                    <div style={{
                      padding: '12px 14px', marginBottom: '16px',
                      background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.25)',
                      borderRadius: '8px', fontSize: '13px', color: '#b91c1c'
                    }}>
                      This agent has no alert channels enabled. They will not be
                      notified of new Users by email or push.
                    </div>
                  )}

                  {/* ---- Email ---- */}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 0', borderBottom: '1px solid #f0f0f0'
                  }}>
                    <div style={{ paddingRight: '16px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#111' }}>
                        Lead alert emails
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                        Turning this off does not stop the assistant address below.
                      </div>
                    </div>
                    <button
                      disabled={notifSaving}
                      onClick={() => saveNotifSettings({ leadEmailEnabled: !notifSettings.leadEmailEnabled })}
                      style={{
                        padding: '6px 14px', fontSize: '12px', fontWeight: 600,
                        borderRadius: '999px', cursor: notifSaving ? 'default' : 'pointer',
                        whiteSpace: 'nowrap',
                        border: `1px solid ${notifSettings.leadEmailEnabled ? '#111' : '#e5e7eb'}`,
                        background: notifSettings.leadEmailEnabled ? '#111' : '#fff',
                        color: notifSettings.leadEmailEnabled ? '#fff' : '#6b7280',
                      }}
                    >
                      {notifSettings.leadEmailEnabled ? 'On' : 'Off'}
                    </button>
                  </div>

                  {/* ---- Push ---- */}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 0', borderBottom: '1px solid #f0f0f0'
                  }}>
                    <div style={{ paddingRight: '16px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#111' }}>
                        Push notifications
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                        {notifSettings.deviceCount === 0
                          ? 'No devices registered — the agent must enable this from the portal on their own device.'
                          : `${notifSettings.deviceCount} device${notifSettings.deviceCount === 1 ? '' : 's'} registered`}
                      </div>
                      {notifSettings.pushEnabledButNoDevices && (
                        <div style={{ fontSize: '12px', color: '#92600a', marginTop: '4px' }}>
                          Enabled, but nothing will be delivered until the agent registers a device.
                        </div>
                      )}
                    </div>
                    <button
                      disabled={notifSaving}
                      onClick={() => saveNotifSettings({ leadPushEnabled: !notifSettings.leadPushEnabled })}
                      style={{
                        padding: '6px 14px', fontSize: '12px', fontWeight: 600,
                        borderRadius: '999px', cursor: notifSaving ? 'default' : 'pointer',
                        whiteSpace: 'nowrap',
                        border: `1px solid ${notifSettings.leadPushEnabled ? '#111' : '#e5e7eb'}`,
                        background: notifSettings.leadPushEnabled ? '#111' : '#fff',
                        color: notifSettings.leadPushEnabled ? '#fff' : '#6b7280',
                      }}
                    >
                      {notifSettings.leadPushEnabled ? 'On' : 'Off'}
                    </button>
                  </div>

                  {/* ---- Assistant email ---- */}
                  <div style={{ padding: '14px 0' }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#111' }}>
                      Assistant email
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px', marginBottom: '4px' }}>
                      Receives a copy of every lead alert for this agent.
                    </div>
                    {/* Stated plainly because it is not obvious: the assistant's
                        copy carries the agent's claim token. */}
                    <div style={{ fontSize: '12px', color: '#92600a', marginBottom: '10px' }}>
                      This grants claim access — whoever holds this inbox can claim
                      Users and spend this agent’s points. Only set it when the
                      agent has asked for it.
                    </div>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="email"
                        value={assistantDraft}
                        onChange={e => setAssistantDraft(e.target.value)}
                        placeholder="assistant@brokerage.com"
                        style={{
                          flex: 1, padding: '9px 12px', fontSize: '13px',
                          border: '1px solid #e5e7eb', borderRadius: '8px', color: '#111'
                        }}
                      />
                      <button
                        disabled={notifSaving || assistantDraft === (notifSettings.assistantEmail || '')}
                        onClick={() => saveNotifSettings({ assistantEmail: assistantDraft.trim() || null })}
                        style={{
                          padding: '9px 16px', fontSize: '12px', fontWeight: 600,
                          borderRadius: '8px', border: 'none', color: '#fff',
                          background: assistantDraft === (notifSettings.assistantEmail || '') ? '#d1d5db' : '#111',
                          cursor: assistantDraft === (notifSettings.assistantEmail || '') ? 'default' : 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Save
                      </button>
                      {notifSettings.assistantEmail && (
                        <button
                          disabled={notifSaving}
                          onClick={() => saveNotifSettings({ assistantEmail: null })}
                          style={{
                            padding: '9px 14px', fontSize: '12px', fontWeight: 600,
                            borderRadius: '8px', border: '1px solid #e5e7eb',
                            background: '#fff', color: '#b91c1c', cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                </div>
              )}

              {activeTab === 'pmPlan' && (() => {
                const draftPlanDef = pmDraftPlan ? pmPlans[pmDraftPlan] : null;
                const dirty = pmBilling && (
                  (pmDraftPlan || null) !== (pmBilling.plan || null) ||
                  (pmDraftCap === '' ? null : Number(pmDraftCap)) !== (pmBilling.monthlySpendCap ?? null)
                );
                const labelStyle = { fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '6px' };
                const inputStyle = { width: '100%', padding: '9px 10px', fontSize: '14px', border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fff', boxSizing: 'border-box' };
                return (
                  <div style={{ maxWidth: '520px' }}>
                    {pmError && (
                      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
                        {pmError}
                      </div>
                    )}

                    {isLoadingPm || !pmBilling ? (
                      <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                        {isLoadingPm ? 'Loading plan...' : ''}
                      </div>
                    ) : (
                      <div style={{ background: '#fafafa', borderRadius: '12px', padding: '16px', border: '1px solid #f0f0f0' }}>
                        <div style={{ marginBottom: '14px' }}>
                          <div style={labelStyle}>Plan</div>
                          <select value={pmDraftPlan} onChange={e => setPmDraftPlan(e.target.value)} style={inputStyle}>
                            <option value="">No plan</option>
                            {Object.entries(pmPlans).map(([key, p]) => (
                              <option key={key} value={key}>
                                {p.label} — ${p.price.toLocaleString()}/mo · {p.includedInquiries} inquiries · ${p.overage} overage
                              </option>
                            ))}
                          </select>
                        </div>

                        <div style={{ marginBottom: '14px' }}>
                          <div style={labelStyle}>Monthly spend cap ($)</div>
                          <input
                            type="number"
                            min="0"
                            value={pmDraftCap}
                            onChange={e => setPmDraftCap(e.target.value)}
                            placeholder={draftPlanDef ? `Plan base — $${draftPlanDef.price.toLocaleString()}` : 'Plan base'}
                            style={inputStyle}
                          />
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                            Leave empty to cap at the plan's base price.
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <button
                            onClick={savePmBilling}
                            disabled={!dirty || isSavingPm}
                            style={{
                              padding: '8px 16px', fontSize: '0.8rem', fontWeight: 600,
                              background: pmSaved ? 'rgba(34,197,94,0.1)' : '#111',
                              color: pmSaved ? '#16a34a' : '#fff',
                              border: pmSaved ? '1px solid rgba(34,197,94,0.25)' : '1px solid #111',
                              borderRadius: '6px', cursor: dirty ? 'pointer' : 'default',
                              opacity: !dirty && !pmSaved ? 0.4 : 1
                            }}
                          >
                            {isSavingPm ? 'Saving...' : pmSaved ? 'Saved' : 'Save'}
                          </button>
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                            Current cap: {pmBilling.effectiveSpendCap != null ? `$${pmBilling.effectiveSpendCap.toLocaleString()}/mo` : '—'}
                            {pmBilling.updatedAt ? ` · updated ${new Date(pmBilling.updatedAt).toLocaleDateString()} by ${pmBilling.updatedBy || '—'}` : ''}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {activeTab === 'integrations' && (
                <div>
                  {integrationsError && (
                    <div style={{
                      background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
                      padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px'
                    }}>{integrationsError}</div>
                  )}

                  {isLoadingIntegrations && (
                    <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                      Loading integrations...
                    </div>
                  )}

                  {!isLoadingIntegrations && integrations.length === 0 && !integrationsError && (
                    <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                      No API keys connected.
                    </div>
                  )}

                  {!isLoadingIntegrations && integrations.map(i => {
                    const revealed = revealedKeys[i.provider];
                    return (
                      <div key={i.provider} style={{
                        background: '#fafafa', borderRadius: '12px', padding: '16px',
                        border: '1px solid #f0f0f0', marginBottom: '12px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px', gap: '12px', flexWrap: 'wrap' }}>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: '#111' }}>{i.provider}</div>
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                            Connected {i.connectedAt ? new Date(i.connectedAt).toLocaleDateString() : '—'}
                            {i.updatedAt && i.updatedAt !== i.connectedAt
                              ? ` · updated ${new Date(i.updatedAt).toLocaleDateString()}` : ''}
                          </div>
                        </div>

                        {/* One row per credential field. Masked from `display` until revealed. */}
                        {Object.keys(revealed ? revealed.credentials : (i.display || {})).map(field => {
                          const label = revealed?.labels?.[field] || field;
                          const value = revealed ? revealed.credentials[field] : i.display[field];
                          const copyKey = `${i.provider}:${field}`;
                          return (
                            <div key={field} style={{ marginBottom: '8px' }}>
                              <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>
                                {label}
                              </div>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                                <div style={{
                                  flex: 1, fontSize: '12px', fontFamily: 'monospace', color: '#111',
                                  background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px',
                                  padding: '8px 10px', wordBreak: 'break-all', lineHeight: 1.5
                                }}>
                                  {value}
                                </div>
                                {revealed && (
                                  <button
                                    onClick={() => copyIntegrationKey(i.provider, field)}
                                    style={{
                                      padding: '0 12px', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap',
                                      background: copiedProvider === copyKey ? 'rgba(34,197,94,0.1)' : '#111',
                                      color: copiedProvider === copyKey ? '#16a34a' : '#fff',
                                      border: copiedProvider === copyKey ? '1px solid rgba(34,197,94,0.25)' : '1px solid #111',
                                      borderRadius: '6px', cursor: 'pointer', transition: 'all 0.15s'
                                    }}
                                  >
                                    {copiedProvider === copyKey ? 'Copied' : 'Copy'}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                          {!revealed ? (
                            <button
                              onClick={() => revealIntegrationKey(i.provider)}
                              disabled={revealingProvider === i.provider}
                              style={{
                                padding: '7px 14px', fontSize: '0.78rem', fontWeight: 600,
                                background: '#fff', color: '#111', border: '1px solid #e5e7eb',
                                borderRadius: '6px', cursor: 'pointer'
                              }}
                            >
                              {revealingProvider === i.provider ? 'Revealing...' : 'Reveal'}
                            </button>
                          ) : (
                            <button
                              onClick={() => setRevealedKeys(prev => { const n = { ...prev }; delete n[i.provider]; return n; })}
                              style={{
                                padding: '7px 14px', fontSize: '0.78rem', fontWeight: 600,
                                background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb',
                                borderRadius: '6px', cursor: 'pointer'
                              }}
                            >
                              Hide
                            </button>
                          )}
                          <button
                            onClick={() => testIntegration(i.provider)}
                            disabled={testingProvider === i.provider}
                            style={{
                              padding: '7px 14px', fontSize: '0.78rem', fontWeight: 600,
                              background: '#111', color: '#fff', border: '1px solid #111',
                              borderRadius: '6px', cursor: 'pointer',
                              opacity: testingProvider === i.provider ? 0.6 : 1
                            }}
                          >
                            {testingProvider === i.provider ? 'Testing...' : 'Test Connection'}
                          </button>
                        </div>

                        {testResults[i.provider] && (() => {
                          const t = testResults[i.provider];
                          return (
                            <div style={{
                              marginTop: '12px', padding: '10px 12px', borderRadius: '8px',
                              background: t.ok ? '#f0fdf4' : '#fef2f2',
                              border: `1px solid ${t.ok ? '#bbf7d0' : '#fecaca'}`
                            }}>
                              <div style={{ fontSize: '13px', fontWeight: 600, color: t.ok ? '#15803d' : '#b91c1c' }}>
                                {t.ok ? '✓ ' : '✕ '}{t.message}{t.status ? ` (${t.status})` : ''}
                              </div>
                              {t.ok && t.fields?.length > 0 && (
                                <div style={{ fontSize: '11px', color: '#374151', marginTop: '6px', lineHeight: 1.5 }}>
                                  <strong>Fields:</strong> {t.fields.join(', ')}
                                </div>
                              )}
                              {(t.sample?.length > 0 || t.raw) && (
                                <details style={{ marginTop: '8px' }}>
                                  <summary style={{ fontSize: '11px', color: '#6b7280', cursor: 'pointer' }}>
                                    {t.ok ? 'Sample rows' : 'Raw response'}
                                  </summary>
                                  <pre style={{
                                    fontSize: '11px', background: '#fff', border: '1px solid #e5e7eb',
                                    borderRadius: '6px', padding: '8px', marginTop: '6px',
                                    maxHeight: '260px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all'
                                  }}>
                                    {t.ok ? JSON.stringify(t.sample, null, 2) : t.raw}
                                  </pre>
                                </details>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              )}

              {activeTab === 'studio' && (
                <div>
                  {studioError && (
                    <div style={{
                      background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
                      padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px'
                    }}>{studioError}</div>
                  )}
                  {studioSuccess && (
                    <div style={{
                      background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d',
                      padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px'
                    }}>{studioSuccess}</div>
                  )}

                  {isLoadingStudio && !studioCredits && (
                    <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                      Loading Studio account...
                    </div>
                  )}

                  {studioCredits && (
                    <>
                      {/* Balance */}
                      <div style={{
                        background: '#fafafa', borderRadius: '12px', padding: '16px',
                        border: '1px solid #f0f0f0', marginBottom: '20px',
                        display: 'flex', gap: '28px', alignItems: 'center', flexWrap: 'wrap'
                      }}>
                        <div>
                          <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', color: '#9ca3af', textTransform: 'uppercase' }}>
                            Credits
                          </div>
                          <div style={{ fontSize: '28px', fontWeight: 700, color: '#111', lineHeight: 1.2 }}>
                            {studioCredits.credits}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', color: '#9ca3af', textTransform: 'uppercase' }}>
                            Subscription
                          </div>
                          <div style={{
                            marginTop: '6px', display: 'inline-block',
                            padding: '4px 10px', borderRadius: '999px',
                            fontSize: '11px', fontWeight: 600,
                            background: studioCredits.studioStatus === 'active' ? '#f0fdf4'
                              : studioCredits.studioStatus === 'past_due' ? '#fffbeb' : '#fef2f2',
                            color: studioCredits.studioStatus === 'active' ? '#15803d'
                              : studioCredits.studioStatus === 'past_due' ? '#b45309' : '#b91c1c',
                          }}>
                            {studioCredits.studioStatus}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', color: '#9ca3af', textTransform: 'uppercase' }}>
                            Last granted
                          </div>
                          <div style={{ fontSize: '13px', color: '#374151', marginTop: '8px' }}>
                            {studioCredits.creditsGrantedAt
                              ? new Date(studioCredits.creditsGrantedAt).toLocaleDateString()
                              : 'never'}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', color: '#9ca3af', textTransform: 'uppercase' }}>
                            Net comped
                          </div>
                          <div style={{ fontSize: '13px', color: '#374151', marginTop: '8px' }}>
                            {studioCredits.netManual > 0 ? '+' : ''}{studioCredits.netManual}
                          </div>
                        </div>
                      </div>

                      {/* Adjust */}
                      <div style={{
                        background: '#fafafa', borderRadius: '12px', padding: '16px',
                        border: '1px solid #f0f0f0', marginBottom: '20px'
                      }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#111', marginBottom: '4px' }}>
                          Adjust credits
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '14px' }}>
                          Negative deducts. Capped at 20 either way. One credit is one video render.
                        </div>

                        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '5px' }}>
                              Amount
                            </label>
                            <input
                              type="number"
                              value={studioDelta}
                              onChange={(e) => { setStudioDelta(e.target.value); setStudioError(''); }}
                              placeholder="+2"
                              style={{
                                width: '90px', padding: '9px 10px', fontSize: '13px',
                                border: '1px solid #e5e7eb', borderRadius: '8px'
                              }}
                            />
                          </div>
                          <div style={{ flex: 1, minWidth: '260px' }}>
                            <label style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '5px' }}>
                              Reason (required, logged)
                            </label>
                            <input
                              type="text"
                              value={studioReason}
                              onChange={(e) => { setStudioReason(e.target.value); setStudioError(''); }}
                              placeholder="comp - failed render, refund didn't fire"
                              style={{
                                width: '100%', padding: '9px 10px', fontSize: '13px',
                                border: '1px solid #e5e7eb', borderRadius: '8px'
                              }}
                            />
                          </div>
                          <button
                            onClick={submitStudioAdjustment}
                            disabled={isAdjustingStudio}
                            style={{
                              padding: '10px 18px', fontSize: '12px', fontWeight: 600,
                              borderRadius: '8px', border: 'none', color: '#fff',
                              background: isAdjustingStudio ? '#d1d5db' : '#111',
                              cursor: isAdjustingStudio ? 'default' : 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {isAdjustingStudio ? 'Applying...' : 'Apply'}
                          </button>
                        </div>
                      </div>

                      {/* History */}
                      <div style={{
                        background: '#fafafa', borderRadius: '12px', padding: '16px',
                        border: '1px solid #f0f0f0'
                      }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#111', marginBottom: '12px' }}>
                          Manual adjustments ({studioCredits.adjustments.length})
                        </div>

                        {studioCredits.adjustments.length === 0 ? (
                          <div style={{ fontSize: '12px', color: '#9ca3af', padding: '12px 0' }}>
                            No manual adjustments. Subscription grants and video charges are not shown here.
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {studioCredits.adjustments.map((adj, i) => (
                              <div key={i} style={{
                                display: 'flex', gap: '12px', alignItems: 'baseline',
                                padding: '9px 0',
                                borderBottom: i < studioCredits.adjustments.length - 1 ? '1px solid #f0f0f0' : 'none'
                              }}>
                                <div style={{
                                  fontSize: '13px', fontWeight: 700, minWidth: '34px',
                                  color: adj.delta > 0 ? '#15803d' : '#b91c1c'
                                }}>
                                  {adj.delta > 0 ? '+' : ''}{adj.delta}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '12px', color: '#374151' }}>{adj.reason}</div>
                                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                    {adj.adminEmail || 'unknown admin'}
                                    {' - '}
                                    {new Date(adj.at).toLocaleString()}
                                    {adj.balanceAfter != null && ` - balance ${adj.balanceAfter}`}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

          </div>

        </div>
      </div>
    </div>
  );
};

export default AgentDetailsModal;